import {
    Callout,
    ComboBox,
    DatePicker,
    DefaultButton,
    DirectionalHint,
    IComboBox,
    IComboBoxOption,
    IconButton,
    Link,
    Modal,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Text,
    TextField,
    TimePicker,
    useTheme
} from "@fluentui/react";
import {
    Alert24Regular,
    CalendarLtr24Regular,
    Clock24Regular,
    DocumentText24Regular,
    Dismiss24Regular,
    Location24Regular,
    Mail24Regular,
    Note24Regular,
    People24Regular,
    Person24Regular,
    Phone24Regular
} from "@fluentui/react-icons";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ChangeStatus, EBookingStatus, ESystemStatus, IEvent } from "../../../interfaces/event";
import { EPublishStatus, shiftsContext } from "../../../providers/shifts";
import { resourcesContext } from "../../../providers/resources";
import { ILinkedError, IResource, ITimeOffRequest, JobTypeLabels, RosterValidationErrorType, statecode } from "../../../interfaces/resource";
import { endOfDay, format, isAfter, isBefore, startOfDay } from "date-fns";
import { buildCrmUrl } from "../../../helpers/crmUrlBuilder";
import { apiContext } from "../../../providers/api";
import { useRsoConfiguration } from "../../../providers/rsoConfigurationContext";
import { renderGenericFallbackUi } from "../../error-boundary/generic-fallback-ui";
import Swal from "sweetalert2";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../../../applicationInsights";
import { toast } from "../../../helpers/notifications";
import { MAX_SUGGESTED_RESOURCES, streamChatGPTRequest, type IAiAllocation } from "../../../helpers/aiEmpower/ChatGPTOptimizer";
import { normalizeUnassignedJob } from "../../../helpers/aiEmpower/normalizeUnassignedJob";
import { resolveResourceTravelAnchor } from "../../../helpers/travel/travelSegments";
import { ITravelMeasureResult, ITravelMeasureResultSegment, ResourceTravelLocationType } from "../../../interfaces/travel";



interface IAuditRecord {
    auditid: string;
    action?: number;
    createdon?: string;
    changedata?: string;
    attributemask?: string;
    _objectid_value?: string;
    _userid_value?: string;
    userid?: {
        fullname?: string;
    };
    '_userid_value@OData.Community.Display.V1.FormattedValue'?: string;
    'action@OData.Community.Display.V1.FormattedValue'?: string;
}

interface IAuditRecordWithEntity extends IAuditRecord {
    entityType: 'workorder' | 'booking';
}

interface IAuditResponse {
    value?: IAuditRecord[];
}

interface IInvitationStatusRow {
    resourceId: string;
    resourceName: string;
    resource: IResource | null;
    status: string;
    statusLabel: string;
    assignmentLabel?: string | null;
}

interface IHistoryTimelineItem {
    key: string;
    date: Date;
    dateLabel: string;
    weekdayLabel: string;
    actorName: string;
    actorInitials: string;
    actorImageUrl?: string;
    action: string;
    note?: string;
}

interface IWorkOrderProductEntry {
    id: string;
    name: string;
}

const BIDDING_UNASSIGNED_SHIFT_IDS_STORAGE_KEY = 'rb_unassigned_bidding_shift_ids_v1';
const BIDDING_OPTIMISATION_COMPLETED_SHIFT_IDS_STORAGE_KEY = 'rb_bidding_optimisation_completed_shift_ids_v1';
type BiddingUnassignedMarker = {
    originalResourceId: string;
    startedAt: string;
};
type BiddingOptimisationCompletedMarker = {
    completedAt: string;
};
type InviteChecklistDraft = {
    selectedResourceIds: string[];
    manualResourceIds: string[];
};

interface IWorkOrderDetailState {
    estimatedTravelDuration?: number | null;
    hourlyRate?: number | null;
    products: IWorkOrderProductEntry[];
    services: IWorkOrderProductEntry[];
}

type StaffDistanceState = {
    distanceLabel: string;
    tooltip: string;
    durationLabel?: string;
};

const STAFF_DISTANCE_UNAVAILABLE_LABEL = 'Unavailable';
const COST_BADGE_FORMATTER = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
});

const toFiniteCoordinateValue = (value: number | string | null | undefined): number | null => {
    if (value == null || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const toCoordinateOrNull = (
    value: {
        latitude?: number | string | null;
        longitude?: number | string | null;
    } | null | undefined
): { latitude: number; longitude: number; } | null => {
    const latitude = toFiniteCoordinateValue(value?.latitude);
    const longitude = toFiniteCoordinateValue(value?.longitude);

    if (latitude == null || longitude == null) {
        return null;
    }

    return { latitude, longitude };
};

const coordinatesEqual = (
    left: { latitude: number; longitude: number; } | null,
    right: { latitude: number; longitude: number; } | null
): boolean => {
    if (!left || !right) {
        return false;
    }

    return Math.abs(left.latitude - right.latitude) < 0.0000001 &&
        Math.abs(left.longitude - right.longitude) < 0.0000001;
};

const formatDistanceKilometres = (distanceMeters: number | null | undefined): string | null => {
    const safeDistance = Number(distanceMeters);
    if (!Number.isFinite(safeDistance) || safeDistance < 0) {
        return null;
    }

    return `${(safeDistance / 1000).toFixed(1)} km`;
};

const formatDurationLabel = (durationSeconds: number | null | undefined): string | null => {
    const safeDuration = Number(durationSeconds);
    if (!Number.isFinite(safeDuration) || safeDuration <= 0) {
        return null;
    }

    const totalMinutes = Math.max(1, Math.round(safeDuration / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
    }

    if (hours > 0) {
        return `${hours}h`;
    }

    return `${minutes}m`;
};

const toUnavailableDistanceState = (tooltip: string): StaffDistanceState => ({
    distanceLabel: STAFF_DISTANCE_UNAVAILABLE_LABEL,
    tooltip
});

const toAvailableDistanceState = (
    distanceMeters: number | null | undefined,
    durationSeconds: number | null | undefined
): StaffDistanceState | null => {
    const distanceLabel = formatDistanceKilometres(distanceMeters);
    if (!distanceLabel) {
        return null;
    }

    const durationLabel = formatDurationLabel(durationSeconds) ?? undefined;

    return {
        distanceLabel,
        durationLabel,
        tooltip: durationLabel
            ? `Approximate OSRM travel time: ${durationLabel}.`
            : 'Distance measured by OSRM.'
    };
};

const isResourceAddressLocation = (resource: IResource): boolean => {
    const normalizedLabel = String(resource.startLocationLabel ?? '').trim().toLowerCase();
    return resource.startLocationType === ResourceTravelLocationType.ResourceAddress ||
        normalizedLabel.includes('resource address');
};

const isOrganizationalUnitLocation = (resource: IResource): boolean => {
    const normalizedLabel = String(resource.startLocationLabel ?? '').trim().toLowerCase();
    return resource.startLocationType === ResourceTravelLocationType.OrganizationalUnitAddress ||
        normalizedLabel.includes('organizational unit address') ||
        normalizedLabel.includes('organisation unit address');
};

const isLocationAgnosticLocation = (resource: IResource): boolean => {
    const normalizedLabel = String(resource.startLocationLabel ?? '').trim().toLowerCase();
    return resource.startLocationType === ResourceTravelLocationType.LocationAgnostic ||
        normalizedLabel.includes('location agnostic');
};

const getShiftSiteDistanceUnavailableReason = (
    event: IEvent | undefined,
    siteLocationAddress: string | undefined
): string => {
    const siteName = String(
        event?.siteLocationDetails?.name ??
        event?.siteLocationDetails?.address ??
        siteLocationAddress ??
        ''
    ).trim();

    return siteName
        ? `Site location "${siteName}" is missing coordinates, so OSRM distance cannot be calculated.`
        : 'This shift site location is missing coordinates, so OSRM distance cannot be calculated.';
};

const getResourceDistanceUnavailableReason = (
    resource: IResource,
    shiftSiteReason: string
): string => {
    if (isLocationAgnosticLocation(resource)) {
        return 'This resource start location is set to Location Agnostic, so there is no origin for OSRM to measure from.';
    }

    if (isResourceAddressLocation(resource) && !toCoordinateOrNull(resource.resourceAddressCoordinates)) {
        return 'This bookable resource or linked user/contact is missing address coordinates, so OSRM distance cannot be calculated.';
    }

    if (isOrganizationalUnitLocation(resource)) {
        return shiftSiteReason;
    }

    if (!String(resource.startLocationLabel ?? '').trim() && resource.startLocationType == null) {
        return 'This resource does not have a start travel location configured, so OSRM distance cannot be calculated.';
    }

    return 'The resource start location could not be resolved, so OSRM distance cannot be calculated.';
};

const getMeasurementUnavailableReason = (status: string): string => {
    switch (String(status ?? '').trim().toLowerCase()) {
        case 'unroutable':
            return 'OSRM could not find a route between the resource start location and the shift site location.';
        case 'error':
            return 'OSRM returned an error while calculating this distance.';
        default:
            return 'OSRM did not return a usable distance for this resource.';
    }
};

const EventModal = ({
    selectedShift,
    setSelectedShift
}: { selectedShift?: string, setSelectedShift: (id: string | undefined) => void }) => {
    const { patch, get, postOrThrow } = useContext(apiContext);
    const { getResource, getAllResources, isUserBackedResource } = useContext(resourcesContext)
    const {
        getShift,
        updateShiftInfo,
        moveShift,
        getEnvironmentInfo,
        siteLocationAddress,
        siteLocationCoordinates,
        getResourceShifts,
        getResourceData,
        publishSingleShift,
        approveRejectCancellationRequest,
        isCancellationReviewPending,
        patchShift,
        createLateClientCancelTimeEntry,
        removeShift,
        siteLocation,
        refreshBiddingSnapshotById,
        getBookingStatusList
    } = useContext(shiftsContext)
    const { openRsoConfiguration, activePresetLabel, clearManualOverrideLevels, activeLevels } = useRsoConfiguration()

    const theme = useTheme()

    const [event, setEvent] = useState<IEvent | undefined>(undefined)
    const [preferredEmployee, setPreferredEmployee] = useState<IResource>()
    const [selectedResource, setSelectedResource] = useState<IResource>()
    const [open, setOpen] = useState(false);
    const [awaitingPublish, setAwaitingPublish] = useState(false);
    const [awaitingSave, setAwaitingSave] = useState(false);
    const [openedDetails, setOpenedDetails] = useState(0);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'details' | 'staff' | 'alerts' | 'notes'>('details');
    const [historyItems, setHistoryItems] = useState<IHistoryTimelineItem[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [title, setTitle] = useState('');
    const [cancellationRequestComments, setCancellationRequestComments] = useState('');
    const [expandedStaffId, setExpandedStaffId] = useState<string | null>(null);
    const [showAllMatches, setShowAllMatches] = useState(false);
    const [aiAllocations, setAiAllocations] = useState<IAiAllocation[]>([]);
    const [aiAllocationsMode, setAiAllocationsMode] = useState<'auto' | 'manual' | null>(null);
    const [aiStatusMessage, setAiStatusMessage] = useState<string | null>(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [assigningResourceId, setAssigningResourceId] = useState<string | null>(null);
    const [invitingResourceId, setInvitingResourceId] = useState<string | null>(null);
    const [autoInviting, setAutoInviting] = useState(false);
    const [startingShiftBidding, setStartingShiftBidding] = useState(false);
    const [refreshingBiddingStatus, setRefreshingBiddingStatus] = useState(false);
    const [showAiRules, setShowAiRules] = useState(false);
    const [inviteSelectionByResourceId, setInviteSelectionByResourceId] = useState<Record<string, boolean>>({});
    const [manualInviteResourceIds, setManualInviteResourceIds] = useState<string[]>([]);
    const [manualInviteSearchText, setManualInviteSearchText] = useState('');
    const [staffDistanceByResourceId, setStaffDistanceByResourceId] = useState<Record<string, StaffDistanceState>>({});
    const manualInviteComboBoxRef = useRef<IComboBox | null>(null);
    const [workOrderDetails, setWorkOrderDetails] = useState<IWorkOrderDetailState>({
        estimatedTravelDuration: null,
        hourlyRate: null,
        products: [],
        services: []
    });
    const aiAbortRef = useRef<AbortController | null>(null);
    const aiSuggestionsByShiftRef = useRef<Record<string, IAiAllocation[]>>({});
    const completedOptimisationByShiftRef = useRef<Record<string, true>>({});
    const lastAiRequestKeyRef = useRef<string | null>(null);
    const inviteChecklistDraftByShiftRef = useRef<Record<string, InviteChecklistDraft>>({});
    const invitationStatusHistoryByShiftRef = useRef<Record<string, IInvitationStatusRow[]>>({});
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const editSectionRef = useRef<HTMLElement | null>(null);
    const aiRulesTargetRef = useRef<HTMLSpanElement | null>(null);
    const openBiddingAcceptanceRefreshIntervalRef = useRef<number | null>(null);
    const lastAcceptedBidderMaterializationRef = useRef<string | null>(null);
    const getShiftRef = useRef(getShift);
    const refreshBiddingSnapshotByIdRef = useRef(refreshBiddingSnapshotById);
    const [titleChangeTimeout, setTitleChangeTimeout] = useState(setTimeout(() => {
    }, 0));

    const [updated, setUpdated] = useState(false);
    const isUnassigned = !event?.resourceId || event.resourceId === '0';
    const isBiddingDebugEnabled = () => {
        if (typeof window === 'undefined') return false;
        try {
            const envToggle = (window as any).__RB_BIDDING_DEBUG;
            if (envToggle === true || envToggle === 1 || envToggle === '1') return true;
            return [
                '1',
                'true'
            ].includes(window.localStorage?.getItem('__RB_BIDDING_DEBUG') ?? window.localStorage?.getItem('rb_bidding_debug') ?? '');
        } catch {
            return false;
        }
    };
    const logBiddingDebug = (message: string, data?: unknown) => {
        if (!isBiddingDebugEnabled()) return;
        if (typeof data === 'undefined') {
            console.log(`[ShiftBiddingDebug] ${message}`);
            return;
        }
        console.log(`[ShiftBiddingDebug] ${message}`, data);
    };
    const logBiddingInfo = (message: string, data?: unknown) => {
        if (!isBiddingDebugEnabled()) return;
        if (typeof data === 'undefined') {
            console.info(`[ShiftBidding] ${message}`);
            return;
        }
        console.info(`[ShiftBidding] ${message}`, data);
    };
    const isLocalBiddingHost = () => {
        if (typeof window === 'undefined') return false;
        const host = String(window.location?.hostname ?? '').trim().toLowerCase();
        return host === 'localhost' || host === '127.0.0.1';
    };
    const getBiddingPollingIntervalMs = () => {
        if (!isLocalBiddingHost()) return 5000;
        try {
            const envToggle = (window as any).__RB_ENABLE_BIDDING_POLLING;
            if (envToggle === true || envToggle === 1 || envToggle === '1') return 5000;
            const enabledFromStorage = [
                '1',
                'true'
            ].includes(window.localStorage?.getItem('__RB_ENABLE_BIDDING_POLLING') ?? window.localStorage?.getItem('rb_enable_bidding_polling') ?? '');
            return enabledFromStorage ? 5000 : 180000;
        } catch {
            return 180000;
        }
    };
    const normalizePersistedBiddingMarkerPayload = (value: unknown, fallbackStartedAt?: string): BiddingUnassignedMarker => {
        const fallback = String(
            fallbackStartedAt ?? new Date().toISOString()
        ).trim();
        const markerStartedAt = String(
            (value as any)?.startedAt ?? ''
        ).trim();
        const startedAt = Number.isNaN(Date.parse(markerStartedAt))
            ? fallback
            : markerStartedAt;
        return {
            originalResourceId: String((value as any)?.originalResourceId ?? '').trim(),
            startedAt
        };
    };
    const getPersistedBiddingUnassignedShiftMarkers = (): { [shiftId: string]: BiddingUnassignedMarker } => {
        if (typeof window === 'undefined') return {};
        try {
            const raw = window.localStorage?.getItem(BIDDING_UNASSIGNED_SHIFT_IDS_STORAGE_KEY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                const legacy: { [shiftId: string]: BiddingUnassignedMarker } = {};
                parsed.forEach((value) => {
                    const shiftId = String(value ?? '').trim();
                    if (!shiftId) return;
                    legacy[shiftId] = {
                        originalResourceId: '',
                        startedAt: new Date().toISOString()
                    };
                });
                return legacy;
            }
            if (parsed && typeof parsed === 'object') {
                const normalized: { [shiftId: string]: BiddingUnassignedMarker } = {};
                Object.entries(parsed).forEach(([shiftId, marker]) => {
                    const normalizedShiftId = String(shiftId ?? '').trim();
                    if (!normalizedShiftId) return;
                    if (typeof marker === 'object' && marker !== null) {
                        normalized[normalizedShiftId] = normalizePersistedBiddingMarkerPayload(marker, new Date().toISOString());
                        return;
                    }
                    normalized[normalizedShiftId] = {
                        originalResourceId: '',
                        startedAt: new Date().toISOString()
                    };
                });
                return normalized;
            }
            return {};
        } catch {
            return {};
        }
    };
    const getPersistedBiddingUnassignedShiftMarker = (shiftId: string): BiddingUnassignedMarker | null => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return null;
        const markers = getPersistedBiddingUnassignedShiftMarkers();
        return markers[normalizedShiftId] ?? null;
    };
    const getPersistedBiddingResourceId = (value: unknown): string => {
        const trimmed = String(value ?? '').trim();
        if (!trimmed || trimmed === '0') {
            return '';
        }
        return trimmed;
    };
    const normalizePersistedBiddingMarker = (shift: IEvent | null | undefined, marker: BiddingUnassignedMarker | null): BiddingUnassignedMarker | null => {
        if (!marker) return null;
        const normalizedMarkerPayload = normalizePersistedBiddingMarkerPayload(marker, marker.startedAt);
        const normalizedOriginalResourceId = getPersistedBiddingResourceId(normalizedMarkerPayload.originalResourceId);
        const fallbackOriginalResourceId = getPersistedBiddingResourceId(shift?.originalResourceID);
        if (normalizedOriginalResourceId || !fallbackOriginalResourceId) {
            return {
                originalResourceId: normalizedOriginalResourceId,
                startedAt: normalizedMarkerPayload.startedAt
            };
        }

        const normalizedShiftId = String(shift?.id ?? '').trim();
        const fallbackMarker: BiddingUnassignedMarker = {
            originalResourceId: fallbackOriginalResourceId,
            startedAt: normalizePersistedBiddingMarkerPayload(marker, new Date().toISOString()).startedAt
        };
        const markers = getPersistedBiddingUnassignedShiftMarkers();
        if (normalizedShiftId) {
            markers[normalizedShiftId] = fallbackMarker;
            setPersistedBiddingUnassignedShiftMarkers(markers);
        }
        logBiddingInfo('marker-normalized:source-event', {
            eventId: normalizedShiftId,
            fallbackOriginalResourceId
        });
        return fallbackMarker;
    };
    const setPersistedBiddingUnassignedShiftMarkers = (markers: { [shiftId: string]: BiddingUnassignedMarker }) => {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            const compact: { [shiftId: string]: BiddingUnassignedMarker } = {};
            Object.entries(markers).forEach(([shiftId, marker]) => {
                const normalizedShiftId = String(shiftId ?? '').trim();
                if (!normalizedShiftId) return;
                const originalResourceId = String(marker?.originalResourceId ?? '').trim();
                const startedAt = String(marker?.startedAt ?? '').trim();
                const validStartedAt = Number.isNaN(Date.parse(startedAt)) ? new Date().toISOString() : startedAt;
                compact[normalizedShiftId] = {
                    originalResourceId,
                    startedAt: validStartedAt
                };
            });
            const entries = Object.entries(compact);
            if (entries.length < 1) {
                window.localStorage.removeItem(BIDDING_UNASSIGNED_SHIFT_IDS_STORAGE_KEY);
                logBiddingInfo('marker-store:cleared', { reason: 'no-markers' });
                return;
            }
            window.localStorage.setItem(BIDDING_UNASSIGNED_SHIFT_IDS_STORAGE_KEY, JSON.stringify(compact));
            logBiddingInfo('marker-store:updated', { markerCount: entries.length });
        } catch {
            // Ignore storage failures.
        }
    };
    const removePersistedBiddingUnassignedShiftMarker = (shiftId: string) => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return;
        const markers = getPersistedBiddingUnassignedShiftMarkers();
        if (!(normalizedShiftId in markers)) return;
        delete markers[normalizedShiftId];
        setPersistedBiddingUnassignedShiftMarkers(markers);
        logBiddingInfo('marker-store:removed', { eventId: normalizedShiftId });
    };
    const markBiddingShiftAsUnassigned = (shiftId: string, originalResourceId: string) => {
        const normalized = String(shiftId ?? '').trim();
        if (!normalized) return;
        const markers = getPersistedBiddingUnassignedShiftMarkers();
        const alreadyTracked = !!markers[normalized];
        markers[normalized] = {
            originalResourceId: getPersistedBiddingResourceId(originalResourceId),
            startedAt: new Date().toISOString()
        };
        setPersistedBiddingUnassignedShiftMarkers(markers);
        logBiddingInfo('handleStartShiftBidding:persisted-unassigned-marked', {
            eventId: normalized,
            reused: alreadyTracked,
            originalResourceId: getPersistedBiddingResourceId(originalResourceId)
        });
    };
    const resolvePersistedBiddingMarker = (shiftId?: string, source?: string): BiddingUnassignedMarker | null => {
        if (!shiftId || typeof window === 'undefined') return null;
        const marker = getPersistedBiddingUnassignedShiftMarker(shiftId);
        if (marker && source) {
            const normalized = String(marker.startedAt ?? '').trim();
            if (!normalized || Number.isNaN(Date.parse(normalized))) {
                logBiddingInfo('marker-validation:startedAt-invalid', {
                    eventId: String(shiftId ?? '').trim(),
                    source,
                    startedAt: marker.startedAt
                });
            }
            logBiddingDebug('marker-resolution', {
                eventId: String(shiftId ?? '').trim(),
                source,
                startedAt: marker.startedAt,
                originalResourceId: marker.originalResourceId,
                normalizedOriginalResourceId: getPersistedBiddingResourceId(marker.originalResourceId)
            });
        }
        return marker;
    };
    const normalizePersistedOptimisationMarkerPayload = (value: unknown, fallbackCompletedAt?: string): BiddingOptimisationCompletedMarker => {
        const fallback = String(fallbackCompletedAt ?? new Date().toISOString()).trim();
        const markerCompletedAt = String((value as any)?.completedAt ?? '').trim();
        return {
            completedAt: Number.isNaN(Date.parse(markerCompletedAt)) ? fallback : markerCompletedAt
        };
    };
    const getPersistedBiddingOptimisationMarkers = (): { [shiftId: string]: BiddingOptimisationCompletedMarker } => {
        if (typeof window === 'undefined') return {};
        try {
            const raw = window.localStorage?.getItem(BIDDING_OPTIMISATION_COMPLETED_SHIFT_IDS_STORAGE_KEY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.reduce((acc, value) => {
                    const shiftId = String(value ?? '').trim();
                    if (!shiftId) return acc;
                    acc[shiftId] = { completedAt: new Date().toISOString() };
                    return acc;
                }, {} as { [shiftId: string]: BiddingOptimisationCompletedMarker });
            }
            if (parsed && typeof parsed === 'object') {
                const normalized: { [shiftId: string]: BiddingOptimisationCompletedMarker } = {};
                Object.entries(parsed).forEach(([shiftId, marker]) => {
                    const normalizedShiftId = String(shiftId ?? '').trim();
                    if (!normalizedShiftId) return;
                    normalized[normalizedShiftId] = normalizePersistedOptimisationMarkerPayload(marker, new Date().toISOString());
                });
                return normalized;
            }
            return {};
        } catch {
            return {};
        }
    };
    const setPersistedBiddingOptimisationMarkers = (markers: { [shiftId: string]: BiddingOptimisationCompletedMarker }) => {
        if (typeof window === 'undefined' || !window.localStorage) return;
        try {
            const compact: { [shiftId: string]: BiddingOptimisationCompletedMarker } = {};
            Object.entries(markers).forEach(([shiftId, marker]) => {
                const normalizedShiftId = String(shiftId ?? '').trim();
                if (!normalizedShiftId) return;
                compact[normalizedShiftId] = normalizePersistedOptimisationMarkerPayload(marker, new Date().toISOString());
            });
            if (Object.keys(compact).length < 1) {
                window.localStorage.removeItem(BIDDING_OPTIMISATION_COMPLETED_SHIFT_IDS_STORAGE_KEY);
                logBiddingInfo('optimisation-marker-store:cleared', { reason: 'no-markers' });
                return;
            }
            window.localStorage.setItem(BIDDING_OPTIMISATION_COMPLETED_SHIFT_IDS_STORAGE_KEY, JSON.stringify(compact));
            logBiddingInfo('optimisation-marker-store:updated', {
                markerCount: Object.keys(compact).length
            });
        } catch {
            // Ignore storage failures.
        }
    };
    const getPersistedBiddingOptimisationMarker = (shiftId?: string, source?: string): BiddingOptimisationCompletedMarker | null => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return null;
        const marker = getPersistedBiddingOptimisationMarkers()[normalizedShiftId] ?? null;
        if (marker && source) {
            logBiddingDebug('optimisation-marker-resolution', {
                eventId: normalizedShiftId,
                source,
                completedAt: marker.completedAt
            });
        }
        return marker;
    };
    const markBiddingOptimisationCompleted = (shiftId: string) => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return;
        const markers = getPersistedBiddingOptimisationMarkers();
        markers[normalizedShiftId] = {
            completedAt: new Date().toISOString()
        };
        setPersistedBiddingOptimisationMarkers(markers);
        logBiddingInfo('optimisation-marker-store:completed', {
            eventId: normalizedShiftId
        });
    };
    const removePersistedBiddingOptimisationMarker = (shiftId: string, reason?: string) => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return;
        const markers = getPersistedBiddingOptimisationMarkers();
        if (!(normalizedShiftId in markers)) return;
        delete markers[normalizedShiftId];
        setPersistedBiddingOptimisationMarkers(markers);
        logBiddingInfo('optimisation-marker-store:removed', {
            eventId: normalizedShiftId,
            reason: reason ?? 'unspecified'
        });
    };
    const getInviteChecklistDraft = (shiftId?: string): InviteChecklistDraft | null => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return null;
        return inviteChecklistDraftByShiftRef.current[normalizedShiftId] ?? null;
    };
    const setInviteChecklistDraft = (shiftId: string, draft: InviteChecklistDraft) => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return;
        inviteChecklistDraftByShiftRef.current[normalizedShiftId] = {
            selectedResourceIds: Array.from(new Set(
                (draft.selectedResourceIds ?? [])
                    .map((resourceId) => String(resourceId ?? '').trim())
                    .filter((resourceId) => resourceId.length > 0)
            )),
            manualResourceIds: Array.from(new Set(
                (draft.manualResourceIds ?? [])
                    .map((resourceId) => String(resourceId ?? '').trim())
                    .filter((resourceId) => resourceId.length > 0)
            ))
        };
        logBiddingDebug('inviteChecklistDraft:updated', {
            eventId: normalizedShiftId,
            selectedResourceIds: inviteChecklistDraftByShiftRef.current[normalizedShiftId].selectedResourceIds,
            manualResourceIds: inviteChecklistDraftByShiftRef.current[normalizedShiftId].manualResourceIds
        });
    };
    const removeInviteChecklistDraft = (shiftId: string, reason?: string) => {
        const normalizedShiftId = String(shiftId ?? '').trim();
        if (!normalizedShiftId) return;
        if (!(normalizedShiftId in inviteChecklistDraftByShiftRef.current)) return;
        delete inviteChecklistDraftByShiftRef.current[normalizedShiftId];
        logBiddingDebug('inviteChecklistDraft:removed', {
            eventId: normalizedShiftId,
            reason: reason ?? 'unspecified'
        });
    };
    const normalizeBookingStatus = (value: unknown): string => String(value ?? '').trim().toLocaleLowerCase();
    const formatBookingStatusLabel = (value: unknown): string => {
        const normalized = normalizeBookingStatus(value);
        if (!normalized) return 'Unknown';
        return normalized
            .split(' ')
            .filter((part) => part.length > 0)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    };
    const resolveWinningBidderResourceIdFromShift = (shift: IEvent | null | undefined): string | null => {
        const bidders = Array.isArray(shift?.bidders) ? (shift?.bidders ?? []) : [];
        const acceptedBidder = bidders.find((bidder: any) => normalizeBookingStatus((bidder as any)?.bookingStatus) === 'accepted');
        const bidderResourceId = String((acceptedBidder as any)?.bookableResourceId ?? '').trim();
        if (bidderResourceId.length > 0) {
            return bidderResourceId;
        }

        const assignedResourceId = String(shift?.resourceId ?? '').trim();
        if (assignedResourceId.length > 0 && assignedResourceId !== '0') {
            return assignedResourceId;
        }

        return null;
    };
    const persistedBiddingUnassignedMarker = resolvePersistedBiddingMarker(event?.id);
    const hasPersistedBiddingUnassignedMarker = persistedBiddingUnassignedMarker !== null;
    const hasCompletedOptimisationForShift = !!(event?.id && completedOptimisationByShiftRef.current[event.id]);
    const toNonEmptyString = (value: unknown): string | null => {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    };
    const areStringArraysEqual = (left: string[], right: string[]) =>
        left.length === right.length && left.every((value, index) => value === right[index]);
    const areSelectionMapsEqual = (
        left: Record<string, boolean>,
        right: Record<string, boolean>
    ) => {
        const leftKeys = Object.keys(left);
        const rightKeys = Object.keys(right);
        if (leftKeys.length !== rightKeys.length) return false;
        return leftKeys.every((key) => left[key] === right[key]);
    };
    const normalizePersistedBiddingResourceId = (value: unknown): string | null => {
        const normalized = getPersistedBiddingResourceId(value);
        return normalized.length < 1 ? null : normalized;
    };
    const isGuidLike = (value: unknown): value is string =>
        typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
    const summarizeInviteResponse = (response: any) => {
        const nested = response?.body ?? response?.data?.body ?? response?.data ?? null;
        return {
            bookingId: toNonEmptyString(
                response?.id
                ?? response?.Id
                ?? response?.bookingId
                ?? response?.BookingId
                ?? response?.bookableresourcebookingid
                ?? response?.BookableResourceBookingId
                ?? nested?.id
                ?? nested?.Id
                ?? nested?.bookingId
                ?? nested?.BookingId
                ?? nested?.bookableresourcebookingid
                ?? nested?.BookableResourceBookingId
            ),
            bookingStatus: toNonEmptyString(
                response?.bookingStatus
                ?? response?.BookingStatus
                ?? response?.bookableResourceStatus
                ?? response?.BookableResourceStatus
                ?? nested?.bookingStatus
                ?? nested?.BookingStatus
                ?? nested?.bookableResourceStatus
                ?? nested?.BookableResourceStatus
            ),
            resourceId: toNonEmptyString(
                response?.bookableResourceId
                ?? response?.BookableResourceId
                ?? response?.resourceId
                ?? response?.ResourceId
                ?? response?._resource_value
                ?? response?._bookableresource_value
                ?? nested?.bookableResourceId
                ?? nested?.BookableResourceId
                ?? nested?.resourceId
                ?? nested?.ResourceId
                ?? nested?._resource_value
                ?? nested?._bookableresource_value
            ),
            workOrderId: toNonEmptyString(
                response?.workOrderId
                ?? response?.WorkOrderId
                ?? response?.job?.id
                ?? response?.Job?.Id
                ?? response?.jobId
                ?? response?.JobId
                ?? nested?.workOrderId
                ?? nested?.WorkOrderId
                ?? nested?.job?.id
                ?? nested?.Job?.Id
                ?? nested?.jobId
                ?? nested?.JobId
            ),
        };
    };
    const resolveInvitationBookingId = async (resourceId: string) => {
        if (!event?.id) return null;
        const workOrderId = event.id;
        const resourceFilterValues = [
            `_msdyn_workorder_value eq '${workOrderId}' and _resource_value eq '${resourceId}'`,
            `_msdyn_workorder_value eq '${workOrderId}' and _bookableresource_value eq '${resourceId}'`
        ];
        let lastError: unknown = null;
        for (let attempt = 0; attempt < resourceFilterValues.length; attempt += 1) {
            const filter = resourceFilterValues[attempt];
            try {
                const bookingResponse = await get('/Entity/bookableresourcebookings', {
                    select: 'bookableresourcebookingid,modifiedon,_resource_value',
                    filter,
                    orderby: 'modifiedon desc',
                    top: 1
                });
                const bookingId = toNonEmptyString(bookingResponse?.value?.[0]?.bookableresourcebookingid);
                if (!bookingId) {
                    logBiddingInfo('notifyInvitationFallback:booking-not-found', {
                        eventId: workOrderId,
                        resourceId,
                        attempt,
                        filter
                    });
                    continue;
                }
                logBiddingInfo('notifyInvitationFallback:booking-lookup-found', {
                    eventId: workOrderId,
                    resourceId,
                    filter,
                    bookingId
                });
                return bookingId;
            } catch (error) {
                lastError = error;
                logBiddingInfo('notifyInvitationFallback:booking-lookup-failed', {
                    eventId: workOrderId,
                    resourceId,
                    attempt,
                    filter,
                    error: (error as any)?.message ?? String(error)
                });
            }
        }
        logBiddingInfo('notifyInvitationFallback:booking-not-found', {
            eventId: workOrderId,
            resourceId,
            error: (lastError as any)?.message ?? String(lastError ?? '')
        });
        return null;
    };
    const buildInvitationNotifyItem = async (resourceId: string, summary: ReturnType<typeof summarizeInviteResponse>) => {
        console.log('[ShiftBidding][buildInvitationNotifyItem] start', {
            eventId: event?.id ?? null,
            resourceId,
            summary
        });
        if (!event?.id) {
            console.log('[ShiftBidding][buildInvitationNotifyItem] skipped-missing-event-id', {
                resourceId,
                summary
            });
            return;
        }
        let invitationBookingId = summary.bookingId;
        console.log('[ShiftBidding][buildInvitationNotifyItem] initial-booking-id', {
            eventId: event.id,
            resourceId,
            summaryBookingId: summary.bookingId,
            invitationBookingId
        });
        if (!isGuidLike(invitationBookingId)) {
            console.log('[ShiftBidding][buildInvitationNotifyItem] booking-id-not-guid-resolving', {
                eventId: event.id,
                resourceId,
                invitationBookingId
            });
            invitationBookingId = await resolveInvitationBookingId(resourceId);
            console.log('[ShiftBidding][buildInvitationNotifyItem] booking-id-after-resolve', {
                eventId: event.id,
                resourceId,
                invitationBookingId
            });
        }
        if (!isGuidLike(invitationBookingId)) {
            console.log('[ShiftBidding][buildInvitationNotifyItem] skipped-missing-booking-id', {
                eventId: event.id,
                resourceId,
                summary,
                invitationBookingId
            });
            logBiddingInfo('buildInvitationNotifyItem:skipped-missing-booking-id', {
                eventId: event.id,
                resourceId,
                responseSummary: summary
            });
            return null;
        }
        const biddingTimeoutMinutes = Math.max(
            1,
            Math.floor(Number(getEnvironmentInfo()?.configuration?.jobBiddingModule?.biddingInvitationsTimeoutInMinutes) || 180)
        );
        const invitationStartedAt = new Date().toISOString();
        const shiftStart = event.start instanceof Date ? event.start.toISOString() : null;
        const shiftEnd = event.end instanceof Date ? event.end.toISOString() : shiftStart;
        const payload = {
            invitationBookingId,
            workOrderId: event.id,
            resourceId,
            title: 'Shift Invitation',
            jobNumber: toNonEmptyString(event.title) ?? event.id,
            message: 'You have a new shift invitation.',
            shiftStart,
            shiftEnd,
            shiftDate: shiftStart,
            invitationStartedAt,
            invitationExpiryAt: new Date(Date.parse(invitationStartedAt) + (biddingTimeoutMinutes * 60 * 1000)).toISOString()
        };
        console.log('[ShiftBidding][buildInvitationNotifyItem] payload-ready', payload);
        return payload;
    };
    const notifyInvitedResources = async (items: Array<{ resourceId: string; summary: ReturnType<typeof summarizeInviteResponse> }>) => {
        if (!event?.id) {
            return {
                body: null,
                errorMessage: null as string | null,
                skippedResourceIds: [] as string[]
            };
        }

        const skippedResourceIds: string[] = [];
        const invitations: any[] = [];

        for (const item of items) {
            const notifyItem = await buildInvitationNotifyItem(item.resourceId, item.summary);
            if (!notifyItem) {
                skippedResourceIds.push(item.resourceId);
                continue;
            }
            invitations.push(notifyItem);
        }

        if (!invitations.length) {
            logBiddingInfo('notifyInvitedResources:skipped-empty-batch', {
                eventId: event.id,
                skippedResourceIds
            });
            return {
                body: null,
                errorMessage: skippedResourceIds.length > 0 ? 'No notification payloads could be prepared for the invited resources.' : null,
                skippedResourceIds
            };
        }

        try {
            logBiddingInfo('notifyInvitedResources:request', {
                eventId: event.id,
                invitationCount: invitations.length,
                resourceIds: invitations.map((invitation) => invitation.resourceId),
                skippedResourceIds
            });
            const notifyResponse = await postOrThrow('/Rostering/shiftbidding/invitations/notify', { invitations });
            const notifyBody = notifyResponse?.body ?? notifyResponse?.Body ?? notifyResponse ?? null;
            logBiddingInfo('notifyInvitedResources:success', {
                eventId: event.id,
                invitationCount: invitations.length,
                skippedResourceIds,
                notifyBody
            });
            return {
                body: notifyBody,
                errorMessage: null as string | null,
                skippedResourceIds
            };
        } catch (error) {
            const errorMessage = getInviteErrorMessage(error);
            logBiddingInfo('notifyInvitedResources:failed', {
                eventId: event.id,
                invitationCount: invitations.length,
                skippedResourceIds,
                error: errorMessage,
                status: (error as any)?.response?.status
            });
            return {
                body: null,
                errorMessage,
                skippedResourceIds
            };
        }
    };

    const runAiSuggestions = (options?: { force?: boolean; requestKeySuffix?: string; source?: 'auto' | 'manual' }) => {
        if (!event?.id) return;
        const requestKeySuffix = options?.requestKeySuffix ?? (isUnassigned ? 'unassigned' : 'assigned');
        const requestKey = `${event.id}-${requestKeySuffix}`;
        if (!options?.force && lastAiRequestKeyRef.current === requestKey) return;

        lastAiRequestKeyRef.current = requestKey;
        setAiLoading(true);
        setAiStatusMessage(null);
        setAiAllocations([]);
        setAiAllocationsMode(options?.source ?? 'auto');
        if (aiAbortRef.current) {
            aiAbortRef.current.abort();
        }
        const controller = new AbortController();
        aiAbortRef.current = controller;

        const basePayload = (typeof window !== 'undefined' ? (window as any).__AIEmpowerPayload : null);
        const jobPayload = {
            ...normalizeUnassignedJob(event),
            hourlyRate: workOrderDetails.hourlyRate ?? null,
            estimatedTravelDuration: workOrderDetails.estimatedTravelDuration ?? null,
            products: workOrderDetails.products,
            services: workOrderDetails.services
        };
        // For current rosterboard, constrain AI to the shift day to reduce payload size.
        // ROSTERBOARD V2 should keep the full calendar-view planning window instead.
        const shiftDate = event.start ? new Date(event.start) : new Date();
        const dayStart = startOfDay(shiftDate);
        const dayEnd = endOfDay(shiftDate);
        const planningWindow = {
            start: dayStart.toISOString(),
            end: dayEnd.toISOString()
        };
        const explanationFallback = {
            includeReasonPerAllocation: true as const,
            comparisonCriteria: [
                "hard feasibility: leave, unavailability, shift conflicts, and legal hour limits",
                "travel feasibility and route continuity",
                "skill compliance including expiry handling",
                "contract and preferred hours alignment",
                "cost minimization and pay exposure",
                "continuity of care",
                "fair distribution of workload"
            ],
            guidance:
                "For every suggested allocation, include a concise human-readable reason that cites the key comparison(s) leading to the suggestion. Reasons should reference feasibility, the active six optimisation goals, and the most important data points used for the decision."
        };

        const toMs = (value?: string | null) => {
            if (!value) return null;
            const ms = new Date(value).getTime();
            return Number.isNaN(ms) ? null : ms;
        };
        const windowStartMs = dayStart.getTime();
        const windowEndMs = dayEnd.getTime();
        const currentWorkOrderId = String(event?.id ?? '').trim();
        const currentBookingId = String((event as any)?.BookingId ?? '').trim();
        const scopedBookings = basePayload?.bookings?.filter((booking: any) => {
            const bookingJobId = String(booking?.jobId ?? '').trim();
            const bookingId = String(booking?.bookingId ?? '').trim();
            if (!isUnassigned && ((currentWorkOrderId && bookingJobId === currentWorkOrderId) || (currentBookingId && bookingId === currentBookingId))) {
                return false;
            }
            const startMs = toMs(booking?.start);
            const endMs = toMs(booking?.end ?? booking?.start);
            if (startMs == null && endMs == null) return false;
            const effectiveEnd = endMs ?? startMs!;
            return startMs! < windowEndMs && effectiveEnd > windowStartMs;
        }) ?? [];

        const payload = basePayload
            ? {
                ...basePayload,
                rsoConstraintLevels: basePayload.rsoConstraintLevels ?? activeLevels,
                planningWindow,
                bookings: scopedBookings,
                unassignedJobs: [jobPayload],
                explanation: basePayload.explanation ?? explanationFallback
            }
            : {
                resources: [],
                bookings: [],
                unassignedJobs: [jobPayload],
                planningWindow,
                rsoConstraintLevels: activeLevels,
                explanation: explanationFallback
            };

        const eventId = event.id;
        logBiddingDebug('runAiSuggestions:start', {
            eventId,
            source: options?.source ?? 'auto',
            requestKeySuffix,
            isUnassigned,
            isJobBidding: event.isJobBidding,
            resourceId: event.resourceId
        });
        const pushStatus = (message: string) => {
            setAiStatusMessage(message);
        };

        streamChatGPTRequest(payload, pushStatus, controller.signal, { preferCandidateSuggestions: true })
            .then((result) => {
                logBiddingDebug('runAiSuggestions:result', { eventId, result });
                const allocations = Array.isArray(result?.allocations) ? result.allocations : [];
                setAiAllocations(allocations);
                if (eventId) {
                    aiSuggestionsByShiftRef.current[eventId] = allocations;
                    if (options?.source === 'manual') {
                        completedOptimisationByShiftRef.current[eventId] = true;
                        markBiddingOptimisationCompleted(eventId);
                        logBiddingInfo('optimisation:completed', {
                            eventId,
                            allocations: allocations.length,
                            requestKeySuffix
                        });
                    }
                }
                logBiddingDebug('runAiSuggestions:success', {
                    eventId,
                    allocations: allocations.length
                });
                pushStatus(allocations.length ? "Matches ready." : "No matches returned.");
            })
            .catch((error) => {
                if ((error as any)?.name === "AbortError") {
                    logBiddingDebug('runAiSuggestions:aborted', { eventId });
                    return;
                }
                logBiddingDebug('runAiSuggestions:chatgpt-error', { eventId, error: (error as any)?.message ?? String(error) });
                logBiddingDebug('runAiSuggestions:error', { eventId, error: (error as any)?.message ?? String(error) });
                setAiAllocations([]);
                pushStatus("AI matching failed. Please try again.");
            })
            .finally(() => {
                setAiLoading(false);
                logBiddingDebug('runAiSuggestions:done', { eventId });
                if (aiAbortRef.current === controller) {
                    aiAbortRef.current = null;
                }
                clearManualOverrideLevels();
            });
    };

    useEffect(() => {
        getShiftRef.current = getShift;
    }, [getShift]);

    useEffect(() => {
        refreshBiddingSnapshotByIdRef.current = refreshBiddingSnapshotById;
    }, [refreshBiddingSnapshotById]);

    const syncOpenBiddingShiftState = async (shiftId: string) => {
        const previousShift = getShiftRef.current(shiftId);
        const previousResourceId = previousShift?.resourceId ?? null;
        const persistedMarker = resolvePersistedBiddingMarker(shiftId, 'open-bidding-refresh-tick');
        const markerOriginalResourceId = normalizePersistedBiddingResourceId(persistedMarker?.originalResourceId) ?? '';

        await refreshBiddingSnapshotByIdRef.current(shiftId);

        const nextShift = getShiftRef.current(shiftId);
        if (!nextShift) return null;

        const acceptedBidderResourceId = resolveWinningBidderResourceIdFromShift(nextShift);
        const shouldKeepUnassigned = !!persistedMarker && !acceptedBidderResourceId;
        const shouldMaterializeAccepted = !!persistedMarker && !!acceptedBidderResourceId;
        const isNowUnassigned = shouldKeepUnassigned
            ? true
            : !nextShift.resourceId || nextShift.resourceId === '0';

        const normalizedEvent = shouldKeepUnassigned
            ? ({
                ...nextShift,
                resourceId: '0',
                isJobBidding: true,
                originalResourceID: markerOriginalResourceId || nextShift.originalResourceID
            } as IEvent)
            : shouldMaterializeAccepted
                ? ({
                    ...nextShift,
                    resourceId: acceptedBidderResourceId,
                    changeStatus: ChangeStatus.UnChanged,
                    originalResourceID: markerOriginalResourceId || nextShift.originalResourceID
                } as IEvent)
                : nextShift;

        setEvent(normalizedEvent);

        if (shouldKeepUnassigned) {
            logBiddingInfo('openBiddingAcceptanceRefresh:kept-unassigned', {
                eventId: shiftId,
                previousResourceId,
                markerOriginalResourceId,
                markerStartedAt: persistedMarker?.startedAt
            });
        }

        if (shouldMaterializeAccepted) {
            if (lastAcceptedBidderMaterializationRef.current !== acceptedBidderResourceId) {
                lastAcceptedBidderMaterializationRef.current = acceptedBidderResourceId;
                removePersistedBiddingUnassignedShiftMarker(shiftId);
                logBiddingInfo('openBiddingAcceptanceRefresh:materialized', {
                    eventId: shiftId,
                    acceptedResourceId: acceptedBidderResourceId,
                    previousResourceId
                });
            }
        }

        return {
            nextShift,
            previousResourceId,
            isNowUnassigned,
            shouldMaterializeAccepted
        };
    };
    const handleRefreshBiddingStatus = async () => {
        if (!event?.id || refreshingBiddingStatus) return;
        setRefreshingBiddingStatus(true);
        try {
            await syncOpenBiddingShiftState(event.id);
        } catch (error) {
            const errorMessage = (error as any)?.message ?? String(error);
            logBiddingInfo('manualBiddingRefresh:failed', {
                eventId: event.id,
                error: errorMessage
            });
            toast('error', `Failed to refresh invitations. ${errorMessage}`);
        } finally {
            setRefreshingBiddingStatus(false);
        }
    };

    useEffect(() => {
        if (selectedShift) {
            openModal(selectedShift);
        }
    }, [selectedShift])

    useEffect(() => {
        if (!open) {
            setSelectedShift(undefined)
            clearState()
        }
    }, [open])

    useEffect(() => {
        if (!open || !event?.id || !event.isJobBidding || !hasPersistedBiddingUnassignedMarker || !isUnassigned) {
            if (openBiddingAcceptanceRefreshIntervalRef.current !== null) {
                window.clearInterval(openBiddingAcceptanceRefreshIntervalRef.current);
                openBiddingAcceptanceRefreshIntervalRef.current = null;
                logBiddingInfo('openBiddingAcceptanceRefresh:stopped', {
                    eventId: event?.id ?? '',
                    reason: !open
                        ? 'modal-closed'
                        : !event?.id
                            ? 'no-shift-id'
                            : !event.isJobBidding
                                ? 'not-open-for-bidding'
                                : !hasPersistedBiddingUnassignedMarker
                                    ? 'no-persistence-marker'
                                    : 'shift-no-longer-unassigned'
                });
            }
            return;
        }

        const shiftId = event.id;
        const intervalMs = getBiddingPollingIntervalMs();
        let isDisposed = false;
        const refreshShift = async () => {
            if (isDisposed) return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                logBiddingInfo('openBiddingAcceptanceRefresh:skipped', {
                    eventId: shiftId,
                    reason: 'tab-hidden'
                });
                return;
            }
            try {
                const refreshResult = await syncOpenBiddingShiftState(shiftId);
                if (!refreshResult || isDisposed) return;

                const {
                    nextShift,
                    previousResourceId,
                    isNowUnassigned,
                    shouldMaterializeAccepted
                } = refreshResult;

                if (shouldMaterializeAccepted) {
                    if (openBiddingAcceptanceRefreshIntervalRef.current !== null) {
                        window.clearInterval(openBiddingAcceptanceRefreshIntervalRef.current);
                        openBiddingAcceptanceRefreshIntervalRef.current = null;
                        logBiddingInfo('openBiddingAcceptanceRefresh:stopped', {
                            eventId: shiftId,
                            reason: 'accepted-by-candidate'
                        });
                    }
                    return;
                }

                if (!nextShift.isJobBidding) {
                    if (openBiddingAcceptanceRefreshIntervalRef.current !== null) {
                        window.clearInterval(openBiddingAcceptanceRefreshIntervalRef.current);
                        openBiddingAcceptanceRefreshIntervalRef.current = null;
                        logBiddingInfo('openBiddingAcceptanceRefresh:stopped', {
                            eventId: shiftId,
                            reason: 'not-open-for-bidding'
                        });
                    }
                    return;
                }

                if (!isNowUnassigned && previousResourceId !== nextShift.resourceId) {
                    if (openBiddingAcceptanceRefreshIntervalRef.current !== null) {
                        window.clearInterval(openBiddingAcceptanceRefreshIntervalRef.current);
                        openBiddingAcceptanceRefreshIntervalRef.current = null;
                        logBiddingInfo('openBiddingAcceptanceRefresh:stopped', {
                            eventId: shiftId,
                            reason: 'assigned-without-marker'
                        });
                    }
                }
            } catch (error) {
                logBiddingInfo('openBiddingAcceptanceRefresh:failed', {
                    eventId: shiftId,
                    error: (error as any)?.message ?? String(error)
                });
            }
        };

        const pollingEnabled = intervalMs > 0;
        logBiddingInfo('openBiddingAcceptanceRefresh:start', {
            eventId: shiftId,
            pollIntervalMs: intervalMs,
            pollingEnabled
        });
        void refreshShift();
        if (!pollingEnabled) {
            return () => {
                isDisposed = true;
            };
        }
        openBiddingAcceptanceRefreshIntervalRef.current = window.setInterval(() => {
            void refreshShift();
        }, intervalMs);

        return () => {
            isDisposed = true;
            if (openBiddingAcceptanceRefreshIntervalRef.current !== null) {
                window.clearInterval(openBiddingAcceptanceRefreshIntervalRef.current);
                openBiddingAcceptanceRefreshIntervalRef.current = null;
                logBiddingInfo('openBiddingAcceptanceRefresh:stopped', {
                    eventId: shiftId,
                    reason: 'effect-cleanup'
                });
            }
        };
    }, [open, event?.id, event?.isJobBidding, hasPersistedBiddingUnassignedMarker, isUnassigned]);

    useEffect(() => {
        if (!open) return;
        setExpandedStaffId(null);
        setShowAllMatches(false);
        setShowAiRules(false);
        setAiAllocations([]);
        setAiAllocationsMode(null);
        setAiStatusMessage(null);
        setAiLoading(false);
        setInvitingResourceId(null);
        setAutoInviting(false);
        setStartingShiftBidding(false);
        setInviteSelectionByResourceId({});
        setManualInviteResourceIds([]);
        setManualInviteSearchText('');
        if (aiAbortRef.current) {
            aiAbortRef.current.abort();
            aiAbortRef.current = null;
        }
    }, [open, event?.id]);

    useEffect(() => {
        if (!open || !event?.id) return;
        if (activeTab === 'staff' && isUnassigned) {
            const cached = aiSuggestionsByShiftRef.current[event.id] ?? [];
            if (cached.length > 0) {
                setAiAllocations(cached);
                setAiStatusMessage((prev) => prev ?? 'Matches ready.');
                return;
            }
            runAiSuggestions({ requestKeySuffix: 'unassigned', source: 'auto' });
            return;
        }
    }, [open, activeTab, event?.id, isUnassigned]);

    useEffect(() => {
        if (!open || !event?.id) {
            setWorkOrderDetails({
                estimatedTravelDuration: null,
                hourlyRate: null,
                products: [],
                services: []
            });
            return;
        }

        let isDisposed = false;
        const toNumberOrNull = (value: unknown): number | null => {
            const numeric = Number(value);
            return Number.isFinite(numeric) ? numeric : null;
        };
        const toTrimmedString = (value: unknown): string => String(value ?? '').trim();
        const dedupeEntries = (entries: IWorkOrderProductEntry[]): IWorkOrderProductEntry[] => {
            const seen = new Set<string>();
            return entries.filter((entry) => {
                const key = entry.name.trim().toLowerCase();
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        };
        const readFormattedLookupName = (row: any, logicalName: string): string => {
            const annotationKey = `_${logicalName}_value@OData.Community.Display.V1.FormattedValue`;
            return toTrimmedString(row?.[annotationKey]);
        };
        const parseEntries = (
            rows: any[],
            idFieldName: string,
            nameSelector: (row: any, index: number) => string
        ): IWorkOrderProductEntry[] => {
            const entries = rows
                .map((row: any, index: number) => {
                    const name = toTrimmedString(nameSelector(row, index));
                    if (!name) return null;
                    return {
                        id: toTrimmedString(row?.[idFieldName]) || `${event.id}-line-${index}`,
                        name
                    };
                })
                .filter((entry: IWorkOrderProductEntry | null): entry is IWorkOrderProductEntry => entry !== null);
            return dedupeEntries(entries);
        };
        const isServiceProductRow = (row: any): boolean => {
            const hasServiceLineLookup = Boolean(
                row?._illumina_servicelineitem_value ||
                row?.['_illumina_servicelineitem_value@OData.Community.Display.V1.FormattedValue']
            );
            const serviceTypeHint = [
                row?.['msdyn_productservicetype@OData.Community.Display.V1.FormattedValue'],
                row?.['msdyn_linetype@OData.Community.Display.V1.FormattedValue'],
                row?.['msdyn_producttype@OData.Community.Display.V1.FormattedValue']
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hasServiceLineLookup || serviceTypeHint.includes('service');
        };
        const readServiceLineItemNameFromWorkOrderRow = (row: any): string => {
            const directValue = toTrimmedString(
                row?.['_illumina_servicelineitem_value@OData.Community.Display.V1.FormattedValue'] ||
                row?.['_illumina_servicelineitemid_value@OData.Community.Display.V1.FormattedValue'] ||
                row?.['illumina_servicelineitemid@OData.Community.Display.V1.FormattedValue'] ||
                row?.['illumina_servicelineitem@OData.Community.Display.V1.FormattedValue'] ||
                row?.illumina_servicelineitem
            );
            if (directValue) return directValue;

            const dynamicFormattedEntry = Object.entries(row ?? {}).find(([key, value]) => {
                const normalizedKey = key.toLowerCase();
                return normalizedKey.includes('servicelineitem')
                    && normalizedKey.includes('@odata.community.display.v1.formattedvalue')
                    && !!toTrimmedString(value);
            });
            return dynamicFormattedEntry ? toTrimmedString(dynamicFormattedEntry[1]) : '';
        };

        const loadWorkOrderDetails = async () => {
            const [workOrderResult, productsResult, servicesResult] = await Promise.allSettled([
                get('/Entity/msdyn_workorders', {
                    filter: `msdyn_workorderid eq '${event.id}'`,
                    top: 1
                }),
                get('/Entity/msdyn_workorderproducts', {
                    select: 'msdyn_workorderproductid,msdyn_name,msdyn_description,_msdyn_product_value,_illumina_servicelineitem_value',
                    filter: `_msdyn_workorder_value eq '${event.id}'`,
                    top: 500
                }),
                get('/Entity/msdyn_workorderservices', {
                    select: 'msdyn_workorderserviceid,msdyn_name,msdyn_description,_illumina_servicelineitem_value,_msdyn_service_value',
                    filter: `_msdyn_workorder_value eq '${event.id}'`,
                    top: 500
                })
            ]);

            if (isDisposed) return;

            const workOrderRow = workOrderResult.status === 'fulfilled'
                ? (workOrderResult.value as any)?.value?.[0]
                : null;
            const rawProductRows = productsResult.status === 'fulfilled'
                ? ((productsResult.value as any)?.value ?? [])
                : [];
            const rawServiceRows = servicesResult.status === 'fulfilled'
                ? ((servicesResult.value as any)?.value ?? [])
                : [];

            const nonServiceProductRows = rawProductRows.filter((row: any) => !isServiceProductRow(row));
            const productEntries = parseEntries(
                nonServiceProductRows,
                'msdyn_workorderproductid',
                (row) =>
                    readFormattedLookupName(row, 'msdyn_product') ||
                    toTrimmedString(row?.msdyn_name) ||
                    toTrimmedString(row?.msdyn_description)
            );
            const productEntriesFromServiceRows = parseEntries(
                rawServiceRows,
                'msdyn_workorderserviceid',
                (row) => readFormattedLookupName(row, 'msdyn_product')
            );
            const fallbackProductEntries = productEntries.length > 0
                ? productEntries
                : (productEntriesFromServiceRows.length > 0
                    ? productEntriesFromServiceRows
                    : parseEntries(
                        rawProductRows,
                        'msdyn_workorderproductid',
                        (row) =>
                            readFormattedLookupName(row, 'msdyn_product') ||
                            toTrimmedString(row?.msdyn_name) ||
                            toTrimmedString(row?.msdyn_description)
                    ));

            const serviceEntriesFromProductRows = parseEntries(
                rawProductRows.filter((row: any) => isServiceProductRow(row)),
                'msdyn_workorderproductid',
                (row) =>
                    toTrimmedString(row?.['_illumina_servicelineitem_value@OData.Community.Display.V1.FormattedValue']) ||
                    readFormattedLookupName(row, 'msdyn_product') ||
                    toTrimmedString(row?.msdyn_name) ||
                    toTrimmedString(row?.msdyn_description)
            );
            const serviceEntriesFromServiceRows = parseEntries(
                rawServiceRows,
                'msdyn_workorderserviceid',
                (row) =>
                    toTrimmedString(row?.['_illumina_servicelineitem_value@OData.Community.Display.V1.FormattedValue']) ||
                    readFormattedLookupName(row, 'msdyn_service') ||
                    readFormattedLookupName(row, 'msdyn_product') ||
                    toTrimmedString(row?.msdyn_name) ||
                    toTrimmedString(row?.msdyn_description)
            );
            const serviceLineItemFromWorkOrder = serviceEntriesFromProductRows.length > 0
                ? ''
                : readServiceLineItemNameFromWorkOrderRow(workOrderRow);
            const serviceEntriesFromWorkOrder = serviceLineItemFromWorkOrder
                ? [{
                    id: `${event.id}-service-line-item`,
                    name: serviceLineItemFromWorkOrder
                }]
                : [];
            const serviceEntries = serviceEntriesFromServiceRows.length > 0
                ? serviceEntriesFromServiceRows
                : (serviceEntriesFromProductRows.length > 0 ? serviceEntriesFromProductRows : serviceEntriesFromWorkOrder);
            const debugTarget = '12497';
            const eventTitle = toTrimmedString(event?.title);
            const workOrderName = toTrimmedString(workOrderRow?.msdyn_name);
            const workOrderId = toTrimmedString(workOrderRow?.msdyn_workorderid ?? event.id);
            const shouldDebugWorkOrder =
                eventTitle === debugTarget ||
                workOrderName === debugTarget ||
                workOrderId === debugTarget;
            if (shouldDebugWorkOrder) {
                logBiddingDebug('workOrderDetails:debug', {
                    eventId: event.id,
                    eventTitle,
                    workOrderId,
                    workOrderName,
                    rawWorkOrderRow: workOrderRow,
                    queryStatus: {
                        workOrder: workOrderResult.status,
                        workOrderProducts: productsResult.status,
                        workOrderServices: servicesResult.status
                    },
                    rawProductRows,
                    rawServiceRows,
                    parsedProducts: fallbackProductEntries,
                    parsedProductsFromServicesTable: productEntriesFromServiceRows,
                    parsedServices: serviceEntries,
                    serviceLineItemFromWorkOrder
                });
            }

            setWorkOrderDetails({
                estimatedTravelDuration: toNumberOrNull(workOrderRow?.msdyn_estimatedtravelduration),
                hourlyRate: toNumberOrNull(workOrderRow?.msdyn_hourlyrate),
                products: fallbackProductEntries,
                services: serviceEntries
            });
        };

        loadWorkOrderDetails().catch((error) => {
            logBiddingDebug('workOrderDetails:load-failed', {
                eventId: event.id,
                error: (error as any)?.message ?? String(error)
            });
            if (isDisposed) return;
            setWorkOrderDetails({
                estimatedTravelDuration: null,
                hourlyRate: null,
                products: [],
                services: []
            });
        });

        return () => {
            isDisposed = true;
        };
    }, [open, event?.id, get]);

    useEffect(() => {
        if (!open || !isEditOpen || activeTab !== 'details') return;

        const container = scrollContainerRef.current;
        const section = editSectionRef.current;
        if (!container || !section) return;

        const containerRect = container.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        const targetTop = sectionRect.top - containerRect.top + container.scrollTop - 12;

        container.scrollTo({ top: targetTop, behavior: 'smooth' });
    }, [open, isEditOpen, activeTab]);

    useEffect(() => {
        if (!open) return;
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTab, open]);

    const openModal = (id: string) => {
        const normalizedId = String(id ?? '').trim();
        if (!normalizedId) {
            return;
        }

        const eventRequested = getShift(normalizedId);
        if (!eventRequested) {
            logBiddingInfo('openModal:missing-shift', { eventId: normalizedId });
            return;
        }

        const persistedMarker = normalizePersistedBiddingMarker(
            eventRequested,
            resolvePersistedBiddingMarker(eventRequested.id, 'open-modal')
        );
        const markerOriginalResourceId = normalizePersistedBiddingResourceId(persistedMarker?.originalResourceId) ?? '';
        const acceptedResourceId = resolveWinningBidderResourceIdFromShift(eventRequested);
        const shouldApplyPersistedUnassignedState = !!persistedMarker && !acceptedResourceId;
        const shouldMaterializeAcceptedBidder = !!persistedMarker && !!acceptedResourceId;
        const nextEvent: IEvent = shouldApplyPersistedUnassignedState
            ? ({
                ...eventRequested,
                isJobBidding: true,
                resourceId: '0',
                originalResourceID: eventRequested.originalResourceID || markerOriginalResourceId
            } as IEvent)
            : shouldMaterializeAcceptedBidder
                ? ({
                    ...eventRequested,
                    resourceId: acceptedResourceId ?? eventRequested.resourceId,
                    changeStatus: ChangeStatus.UnChanged,
                    originalResourceID: markerOriginalResourceId || eventRequested.originalResourceID
                } as IEvent)
                : eventRequested;

        if (persistedMarker) {
            if (shouldApplyPersistedUnassignedState) {
                logBiddingInfo('openModal:bidding-unassigned-restored', {
                    eventId: normalizedId,
                    markerStartedAt: persistedMarker.startedAt,
                    markerOriginalResourceId,
                    sourceResourceId: eventRequested.resourceId
                });
            } else if (shouldMaterializeAcceptedBidder) {
                logBiddingInfo('openModal:bidding-acceptance-materialized', {
                    eventId: normalizedId,
                    acceptedResourceId,
                    markerOriginalResourceId,
                    sourceResourceId: eventRequested.resourceId
                });
            } else {
                logBiddingInfo('openModal:bidding-marker-no-state-change', {
                    eventId: normalizedId,
                    markerStartedAt: persistedMarker.startedAt
                });
            }
        } else {
            logBiddingInfo('openModal:shift-loaded', {
                eventId: normalizedId,
                resourceId: eventRequested.resourceId,
                isJobBidding: eventRequested.isJobBidding
            });
        }

        setEvent(nextEvent);
        const unassigned = !nextEvent.resourceId || nextEvent.resourceId === '0';
        const resource = shouldMaterializeAcceptedBidder
            ? getResource(acceptedResourceId ?? '')
            : getResource(nextEvent.resourceId);
        if (resource) {
            setSelectedResource(resource)
        } else {
            setSelectedResource(undefined)
        }
        if (nextEvent.resourceId !== nextEvent.preferredResourceId) {
            const preferred = getResource(nextEvent.preferredResourceId)
            if (preferred) {
                setPreferredEmployee(preferred)
            }
        } else {
            if (resource) {
                setPreferredEmployee(resource)
            }
        }

        setTitle(nextEvent.description)
        setIsEditOpen(false)
        setActiveTab(unassigned ? 'staff' : 'details')
        setOpen(true)

        if (shouldApplyPersistedUnassignedState) {
            updateShiftInfo(normalizedId, {
                resourceId: '0',
                isJobBidding: true,
                originalResourceID: nextEvent.originalResourceID || eventRequested.originalResourceID || markerOriginalResourceId,
                changeStatus: eventRequested.changeStatus ?? ChangeStatus.Modified
            });
        }
    }

    const updateEvent = async (newData: Partial<IEvent>) => {
        if (!event) {
            return
        }

        setUpdated(true);

        setEvent((prev) => {
            return {
                loadingLock: true,
                ...prev,
                ...newData,
                detailsModified: true,
                changeStatus: ChangeStatus.Modified,
            } as IEvent
        })

        if (typeof newData.isJobBidding === 'boolean') {
            await patch('/Entity/msdyn_workorders/' + event.id, {
                illumina_openforshiftjobbidding: newData.isJobBidding,
            })
        }

        setEvent((prev) => {
            if (prev) {
                return {
                    loadingLock: false,
                    ...prev,
                } as IEvent
            }
        })

        updateShiftInfo(event.id, {
            ...newData,
            detailsModified: true,
            changeStatus: ChangeStatus.Modified,
        })
    }

    const clearState = (newEvent?: Partial<IEvent>) => {
        if (awaitingPublish) return;

        if (event && updated) {
            updateShiftInfo(event.id, { ...event, ...newEvent })
        }
        lastAiRequestKeyRef.current = null;

        setEvent(undefined)
        setSelectedResource(undefined)
        setPreferredEmployee(undefined)
        setOpenedDetails(0)
        setIsEditOpen(false)
        setActiveTab('details')
        setHistoryItems([])
        setHistoryLoading(false)
        setHistoryError(null)
        setTitle('');
        setStartingShiftBidding(false);
        setInviteSelectionByResourceId({});
        setManualInviteResourceIds([]);
        setManualInviteSearchText('');
        clearTimeout(titleChangeTimeout);
        setUpdated(false);
        if (openBiddingAcceptanceRefreshIntervalRef.current !== null) {
            window.clearInterval(openBiddingAcceptanceRefreshIntervalRef.current);
            openBiddingAcceptanceRefreshIntervalRef.current = null;
        }
    }

    const handlePublish = async () => {
        if (awaitingPublish) return;

        setAwaitingPublish(true);

        const response = await publishSingleShift(event);

        if (response.status === EPublishStatus.success) {
            if (event?.detailsModified) {
                await handleSaveShift({ keepChangeStatus: false });
            }
            setOpen(false);
        }

        setAwaitingPublish(false);
    }

    const handleChangeStart = (e: React.FormEvent<IComboBox>, time?: Date) => {
        if (time && event) {
            const newStart = new Date(event.start.getTime());
            newStart.setHours(time.getHours());
            newStart.setMinutes(time.getMinutes());

            const newEnd = new Date(newStart.getTime());
            newEnd.setMinutes(newEnd.getMinutes() + (event.shiftLength))

            const data: Partial<IEvent> = {
                start: newStart,
                end: newEnd,
            }
            updateEvent(data)
        }
    }

    const handleChangeDuration = (e: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
        if (event?.start && option) {
            const copiedDate = new Date(event?.start.getTime());

            copiedDate.setMinutes(copiedDate.getMinutes() + (option?.key as number))

            const data: Partial<IEvent> = {
                end: copiedDate,
                shiftLength: (option?.key as number)
            }
            updateEvent(data)
        }
    }

    const handleChangeDate = (date: Date | null | undefined) => {
        if (date) {
            const newStart = new Date(date.getTime());
            const newEnd = new Date(date.getTime());

            if (event) {
                newStart.setHours(event.start.getHours())
                newStart.setMinutes(event.start.getMinutes())

                newEnd.setHours(newStart.getHours() + event.shiftLength / 60);
            }

            const data: Partial<IEvent> = {
                start: newStart,
                end: newEnd,
            }

            updateEvent(data);

        }
    }

    const handleUpdateTitle = () => {
        clearTimeout(titleChangeTimeout);
        const data: Partial<IEvent> = {
            description: title,
        }

        updateEvent(data);
    }

    const handleChangeTitle = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setTitle(event.currentTarget.value ?? '');
    }

    const handleTitleInputEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleUpdateTitle();
        }
    }
    const isCancellationRequestInFuture = (date: Date | undefined): boolean => {
        var now = new Date();
        return date != undefined && date > now;
    }

    useEffect(() => {
        if (title !== event?.description) {
            clearTimeout(titleChangeTimeout)
            setTitleChangeTimeout(setTimeout(handleUpdateTitle, 2000))
        }
    }, [title])

    const handleToggleDetails = (event: React.MouseEvent<HTMLElement>, value: number) => {
        event.preventDefault();
        const newOpenedDetails = openedDetails === value ? 0 : value;
        setOpenedDetails(newOpenedDetails);
    }

    const handleSaveShift = async (options?: { keepChangeStatus?: boolean }) => {
        if (!event) return
        setAwaitingSave(true);
        const getNewEvent = await fetchBookingByJobId(event.id);
        await patchShift(event?.id, getNewEvent)
        setAwaitingSave(false)

        setUpdated(true);

        const keepChangeStatus = options?.keepChangeStatus ?? true;
        const refreshedShift = getShift(event.id) ?? getNewEvent;
        const newData = {
            ...refreshedShift,
            detailsModified: false,
            changeStatus: keepChangeStatus
                ? ChangeStatus.Modified
                : (refreshedShift.changeStatus ?? ChangeStatus.UnChanged),
        };

        setEvent(newData as IEvent);

        updateShiftInfo(event.id, newData)
    }

    const checkOverlapingWithLeavesRequest = (
        intervals: ITimeOffRequest[], 
        startTime: Date, 
        endTime: Date
    )=> {
        const inputStart = new Date(startTime);
        const inputEnd = new Date(endTime);
        
        for (const interval of intervals) {
            const intervalStart = new Date(interval.start);
            const intervalEnd = new Date(interval.end);
            
            if (
                (isAfter(inputStart, intervalStart) && isBefore(inputStart, intervalEnd)) ||
                (isAfter(inputEnd, intervalStart) && isBefore(inputEnd, intervalEnd)) ||
                (inputStart <= intervalStart && inputEnd >= intervalEnd) ||
                (intervalStart <= inputStart && intervalEnd >= inputEnd)
            ) {
                return true;
            }
        }
        
        return false;
    }
    

    const fetchBookingByJobId = async (workOrderId: string) => {
        const booking = await get('/Entity/bookableresourcebookings', {
            select: 'name,bookableresourcebookingid,_bookingstatus_value',
            expand: `msdyn_workorder($select=msdyn_systemstatus)`,
            filter: `_msdyn_workorder_value eq '${workOrderId}'`
        });

        let bookingId = "";
        let jobStatus = event?.status;
        if (booking?.value?.length > 0) {
            jobStatus = booking.value[0]?.msdyn_workorder.msdyn_systemstatus;
            // only update booking id if the job status is scheduled or in progress
            if (jobStatus === ESystemStatus.Scheduled || jobStatus === ESystemStatus["In Progress"]) {
                const filteredBooking = booking.value.filter((x: any) => x['_bookingstatus_value@OData.Community.Display.V1.FormattedValue'] !== 'Canceled');
                if (filteredBooking.length > 0) {
                    bookingId = filteredBooking[0].bookableresourcebookingid
                }
            }
        }

        const newEvent = {
            ...event,
            status: jobStatus,
            BookingId: bookingId
        } as IEvent
        
        return newEvent;
    }

    const lateClientCancel = async (event: IEvent) => {
        let shift = getShift(selectedShift as string)
        if (shift?.changeStatus == ChangeStatus.Modified) {
            toast('error', 'The shift is not published yet. Please publish before you cancel the shift.');
            return;
        }

        const confirmation = await Swal.fire({
            title: 'You are about to late cancel. Are you sure you want to late cancel this shift?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: theme.palette.themePrimary,
            cancelButtonColor: theme.palette.tealLight,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',

        })
        if (!confirmation.isConfirmed)
            return

        setAwaitingSave(true);
        await createLateClientCancelTimeEntry(event);
        await patchShift(event.id, { status: ESystemStatus.Canceled, isClientLateCancel: true });
        removeShift(event.id);
        setAwaitingSave(false);
        setOpen(false);
    }
    const cancelShift = async (event: IEvent) => {
        let shift = getShift(selectedShift as string)
        if (shift?.changeStatus == ChangeStatus.Modified) {
            toast('error', 'The shift is not published yet. Please publish before you cancel the shift.');
            return;
        }

        const confirmation = await Swal.fire({
            title: 'Are you sure you want to cancel this shift?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: theme.palette.themePrimary,
            cancelButtonColor: theme.palette.tealLight,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',

        })
        if (confirmation.isConfirmed) {
            await patchShift(event.id, { status: ESystemStatus.Canceled })
            removeShift(event.id);
            setOpen(false);
        }
    }

    const approveRejectShift = async (approve: boolean) => {
        if (!event) return;

        let shift = getShift(selectedShift as string)
        if (shift?.changeStatus == ChangeStatus.Modified) {
            toast('error', 'Please publish before you approve/reject the shift;');
            return;
        }

        setAwaitingSave(true);
        let success = await approveRejectCancellationRequest(event, approve, cancellationRequestComments);
        setAwaitingSave(false);
        if (!success) {
            toast("error", 'Failed to approve/reject. Please refresh and try again.');
            return;
        }

        if (approve) {
            moveShift(event.id, '0', true);
            let partialShift = {
                ...event,
                resourceId: '0',
                originalResourceID: '0',
                bookableResourceStatus: EBookingStatus.Scheduled,
                ChangeStatus: ChangeStatus.UnChanged
            };

            updateShiftInfo(event.id, partialShift);
            setEvent(partialShift);
        }
        else {
            event.isCancellationRequestPendingOriginal = false;
            updateShiftInfo(event.id, event);
            setEvent(event);
        }

        toast('success', approve ? 'Shit is cancelled and unassigned' : 'Shift cancellation request declined.');
    }
    const handleCancellationRequestCommentsChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCancellationRequestComments(e.currentTarget.value)
    }
    const cancellationRequestInFuture = isCancellationRequestInFuture(event?.start);
    const placeholder = 'N/A';
    const formatAllocationMoney = (value: number | null | undefined): string => {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return placeholder;
        }

        return COST_BADGE_FORMATTER.format(Math.max(0, value));
    };
    const getPenaltyTooltip = (allocation?: IAiAllocation): string | undefined => {
        if (!allocation) {
            return undefined;
        }

        const reasons = Array.isArray(allocation.penalties)
            ? allocation.penalties.filter((reason) => !!reason?.trim())
            : [];
        const penaltyCount = allocation.penaltyCount ?? reasons.length ?? 0;

        if (penaltyCount < 1) {
            return 'No penalties applied.';
        }

        if (reasons.length > 0) {
            return `${penaltyCount} penalty${penaltyCount === 1 ? '' : 'ies'}: ${reasons.join(', ')}`;
        }

        return `${penaltyCount} penalty${penaltyCount === 1 ? '' : 'ies'} applied by the optimizer.`;
    };
    const getStaffDistanceState = (resource: IResource): StaffDistanceState | null => {
        const normalizedResourceId = String(resource?.id ?? '').trim();
        if (!normalizedResourceId) {
            return null;
        }

        const existingState = staffDistanceByResourceId[normalizedResourceId];
        if (existingState) {
            return existingState;
        }

        if (!event) {
            return null;
        }

        const shiftSiteUnavailableReason = getShiftSiteDistanceUnavailableReason(event, siteLocationAddress);
        const shiftCoordinate = toCoordinateOrNull(event.siteLocationDetails) ?? toCoordinateOrNull(siteLocationCoordinates);
        if (!shiftCoordinate) {
            return toUnavailableDistanceState(shiftSiteUnavailableReason);
        }

        const matchingResourceSite = (resource.siteLocation ?? []).find((location) =>
            String(location.illumina_sitelocationid ?? '').trim() === String(event.siteLocationDetails?.id ?? '').trim()
        );
        const organizationalUnitFallbackCoordinate = matchingResourceSite
            ? {
                latitude: toFiniteCoordinateValue(matchingResourceSite.illumina_latitudes),
                longitude: toFiniteCoordinateValue(matchingResourceSite.illumina_longitudes)
            }
            : shiftCoordinate;

        const startAnchor = resolveResourceTravelAnchor(
            resource,
            resource.startLocationType,
            resource.startLocationLabel,
            shiftCoordinate,
            organizationalUnitFallbackCoordinate
        );

        if (!startAnchor) {
            return toUnavailableDistanceState(
                getResourceDistanceUnavailableReason(resource, shiftSiteUnavailableReason)
            );
        }

        if (coordinatesEqual(startAnchor, shiftCoordinate)) {
            return {
                distanceLabel: '0.0 km',
                durationLabel: '0m',
                tooltip: 'The resource start location and the shift site location use the same coordinates.'
            };
        }

        return {
            distanceLabel: 'Calculating...',
            tooltip: 'Calculating OSRM distance from the configured resource start location to the shift site.'
        };
    };
    const shiftIdLabel = event?.title || event?.serviceAccount?.accountNumber || event?.id || placeholder;
    const shiftSummary = event?.description || placeholder;
    const shiftDateLabel = event
        ? format(event.start ?? new Date(), 'EEE, MMM d, yyyy')
        : placeholder;
    const shiftTimeLabel = event
        ? `${format(event.start ?? new Date(), 'hh:mm a')} - ${format(event.end ?? new Date(), 'hh:mm a')}`
        : placeholder;
    const locationLabel = event?.siteLocationDetails?.address || siteLocationAddress || event?.serviceAccount?.address1_composite || placeholder;
    const serviceContactName = event?.serviceContact?.fullName || placeholder;
    const normalizedJobType = String(event?.jobType ?? '').trim().toLowerCase();
    const isSilWorkOrder =
        normalizedJobType === String(JobTypeLabels.ResidentialDisabilityandAgedCareService).toLowerCase() ||
        normalizedJobType === '390950003';
    const isIndividualWorkOrder =
        normalizedJobType === String(JobTypeLabels.IndividualService).toLowerCase() ||
        normalizedJobType === '390950000';
    const clientName = isIndividualWorkOrder
        ? serviceContactName
        : (event?.serviceAccount?.name || placeholder);
    const siteTypeLabel = event?.workOrderType?.msdyn_name || placeholder;
    const staffPreferenceLabel = preferredEmployee?.title || event?.preferredResourceName || 'Not provided';
    const activeSkills = event?.incidentType?.characteristic
        ?.filter((char) => char.status !== statecode.Inactive)
        ?.map((char) => char.name) ?? [];
    const listProductsLabel = workOrderDetails.products.length > 0
        ? workOrderDetails.products.map((entry) => entry.name).join(', ')
        : 'No products listed for this shift.';
    const listServicesLabel = workOrderDetails.services.length > 0
        ? workOrderDetails.services.map((entry) => entry.name).join(', ')
        : 'No services listed for this shift.';
    const formatDurationMinutes = (minutes: number | null | undefined): string => {
        const safeMinutes = Number(minutes);
        if (!Number.isFinite(safeMinutes) || safeMinutes <= 0) return 'Not provided';
        const hours = Math.floor(safeMinutes / 60);
        const mins = Math.round(safeMinutes % 60);
        if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
        if (hours > 0) return `${hours}h`;
        return `${mins}m`;
    };
    const estimatedTravelDurationLabel = formatDurationMinutes(workOrderDetails.estimatedTravelDuration);
    const safeSummary = shiftSummary && shiftSummary !== placeholder ? shiftSummary : 'Not provided';
    const siteLocationLabel = event?.siteLocationDetails?.name || siteLocation || locationLabel;
    const detailInformationRows: Array<{ label: string; value: string; span?: number }> = isSilWorkOrder
        ? [
            { label: 'Site location', value: siteLocationLabel || 'Not provided', span: 1 },
            { label: 'Work order type', value: siteTypeLabel || 'Not provided', span: 1 },
            { label: 'Preferred staff', value: staffPreferenceLabel, span: 1 },
            { label: 'Job description', value: safeSummary, span: 3 },
            { label: 'Products', value: listProductsLabel, span: 3 },
            { label: 'Services', value: listServicesLabel, span: 3 }
        ]
        : isIndividualWorkOrder
            ? [
                { label: 'Client Name', value: serviceContactName || 'Not provided', span: 1 },
                { label: 'Work order type', value: siteTypeLabel || 'Not provided', span: 1 },
                { label: 'Shift title', value: event?.title || 'Not provided', span: 1 },
                { label: 'Estimated Travel Duration BRB', value: estimatedTravelDurationLabel, span: 1 },
                { label: 'Preferred staff', value: staffPreferenceLabel, span: 2 },
                { label: 'Job description', value: safeSummary, span: 3 },
                { label: 'Products', value: listProductsLabel, span: 3 },
                { label: 'Services', value: listServicesLabel, span: 3 }
            ]
            : [
                { label: 'Work order type', value: siteTypeLabel || 'Not provided', span: 1 },
                { label: 'Shift title', value: event?.title || 'Not provided', span: 1 },
                { label: 'Preferred staff', value: staffPreferenceLabel, span: 1 },
                { label: 'Job description', value: safeSummary, span: 3 },
                { label: 'Products', value: listProductsLabel, span: 3 },
                { label: 'Services', value: listServicesLabel, span: 3 }
            ];
    const envInfo = getEnvironmentInfo();
    const mapToErrorType = (value: string): RosterValidationErrorType | null =>
        RosterValidationErrorType[value as keyof typeof RosterValidationErrorType] ?? null;
    const configuredHardTypes = envInfo?.configuration?.rosterBoardModule?.showHardAlertsOnlyFor
        ?.map(mapToErrorType)
        ?.filter((value): value is RosterValidationErrorType => value !== null) ?? [];
    const hardTypesFromEnv = envInfo?.hardErrorTypes
        ?.map(mapToErrorType)
        ?.filter((value): value is RosterValidationErrorType => value !== null) ?? [];
    const defaultHardAlertTypes: RosterValidationErrorType[] = [
        RosterValidationErrorType.ExceededMaxWorkingHours,
        RosterValidationErrorType.ConflictBooking,
        RosterValidationErrorType.Unavailable,
        RosterValidationErrorType.Unknown,
    ];
    const hardAlertTypes = configuredHardTypes.length > 0
        ? configuredHardTypes
        : (hardTypesFromEnv.length > 0 ? hardTypesFromEnv : defaultHardAlertTypes);
    const linkedErrors = event?.linkedErrors ?? [];
    const fallbackAlerts: ILinkedError[] = linkedErrors.length === 0 && event?.errors?.length
        ? event.errors.map((message, index) => ({
            message,
            referenceId: `${event?.id ?? 'shift'}-${index}`,
            title: 'Alert',
            entityName: '',
            errorType: RosterValidationErrorType.Unknown,
        }))
        : [];
    const baseShiftAlerts = linkedErrors.length > 0 ? linkedErrors : fallbackAlerts;
    const currentResourceData = event?.resourceId && event.resourceId !== '0'
        ? getResourceData(event.resourceId)
        : null;
    const shiftDurationHours = (event?.shiftLength ?? 0) / 60;
    // Keep this aligned with EventRender.isOvertime() to avoid icon-vs-alert mismatch.
    const overtimeByResourceCap = !!currentResourceData && (
        currentResourceData.allSitesHoursWorked > currentResourceData.maxHoursAllowedPerPayroll ||
        (!!currentResourceData.maxHoursPerShift &&
            shiftDurationHours > currentResourceData.maxHoursPerShift)
    );
    const showOvertimeIconSignal = !!event && (event.isOverTime || overtimeByResourceCap);
    const hasOvertimeAlert = baseShiftAlerts.some((alert) =>
        alert.errorType === RosterValidationErrorType.OverTime ||
        /overtime/i.test(alert.title || '') ||
        /overtime/i.test(alert.message || '')
    );
    const supplementalOvertimeAlert: ILinkedError[] = showOvertimeIconSignal && !hasOvertimeAlert
        ? [{
            message: 'Overtime applies to this shift based on current roster limits.',
            referenceId: `${event?.id ?? 'shift'}-overtime-supplement`,
            title: 'Overtime',
            entityName: '',
            errorType: RosterValidationErrorType.OverTime
        }]
        : [];
    const shiftAlerts = [...baseShiftAlerts, ...supplementalOvertimeAlert];
    const hourlyRate = Number(workOrderDetails.hourlyRate);
    const overtimeBaseRate = Number.isFinite(hourlyRate) ? hourlyRate * 1.5 : null;
    const minimumBreakPattern = /minimum\s+of\s+\d+\s*hours?\s+break\s+between\s+shifts/i;
    const renderAlertMessage = (message: string) => {
        if (!minimumBreakPattern.test(message) || /company will have to pay/i.test(message)) {
            return message;
        }
        const additionalMessage = overtimeBaseRate != null
            ? `Company will have to pay $ ${overtimeBaseRate.toFixed(2)} hourly base.`
            : 'Company will have to pay 150% of the hourly base rate.';
        return (
            <>
                <span>{message}</span>{' '}
                <strong className={'alerts-inline-highlight'}>{additionalMessage}</strong>
            </>
        );
    };
    const alertsCount = shiftAlerts.length;
    const softAlerts = shiftAlerts.filter((alert) => !hardAlertTypes.includes(alert.errorType));
    const hardAlerts = shiftAlerts.filter((alert) => hardAlertTypes.includes(alert.errorType));
    const groupAlerts = (alerts: ILinkedError[]) => {
        const groups = new Map<string, ILinkedError[]>();
        alerts.forEach((alert) => {
            const key = (alert.title || 'Alert').trim() || 'Alert';
            const current = groups.get(key) ?? [];
            current.push(alert);
            groups.set(key, current);
        });
        return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
    };
    const softAlertGroups = groupAlerts(softAlerts);
    const hardAlertGroups = groupAlerts(hardAlerts);
    const showServiceRequirements = false; // Hidden for now per UX request.
    const showCostBreakdown = false; // Hidden for now per UX request.
    const costTotal = event?.displayCost ?? event?.actualCost;
    const allResources = getAllResources();
    const assignedResource = (event?.resourceId && event.resourceId !== '0' && event.resourceId !== '')
        ? (selectedResource?.id === event.resourceId ? selectedResource : (getResource(event.resourceId) || undefined))
        : undefined;
    const assignedStaffLabel = assignedResource?.title || (event?.resourceId && event.resourceId !== '0' && event.resourceId !== '' ? placeholder : 'Unassigned');
    const assignedStaffName = assignedStaffLabel === 'Unassigned' ? placeholder : assignedStaffLabel;
    const requiredSkillsLower = activeSkills.map((skill) => skill.toLowerCase());
    const getInitials = (name: string) =>
        name
            .split(' ')
            .map((part) => part[0])
            .filter(Boolean)
            .slice(0, 2)
            .join('')
            .toUpperCase() || 'SW';
    const assignedInitials = getInitials(assignedStaffName);
    const assignedEmail = assignedResource?.primaryEmail || placeholder;
    const assignedPhone = assignedResource?.primaryPhone || placeholder;
    const assignedPhoneDial = assignedResource?.primaryPhone?.replace(/[^+\d]/g, '') || '';
    const auditActionLabels: Record<number, string> = {
        1: 'Create',
        2: 'Update',
        3: 'Delete',
        13: 'Assign',
        16: 'Close',
        17: 'Cancel',
    };
    const interestingFieldLabels: Record<string, string> = {
        _resource_value: 'Assigned staff',
        resourceid: 'Assigned staff',
        msdyn_systemstatus: 'Shift status',
        _bookingstatus_value: 'Booking status',
        _msdyn_preferredresource_value: 'Preferred staff',
    };
    const guidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
    const getAuditActionLabel = (audit: IAuditRecord): string => {
        const formatted = audit['action@OData.Community.Display.V1.FormattedValue'];
        if (formatted && formatted.trim().length > 0) return formatted;
        if (audit.action && auditActionLabels[audit.action]) return auditActionLabels[audit.action];
        return 'Update';
    };
    const getAuditActorName = (audit: IAuditRecord): string =>
        audit['_userid_value@OData.Community.Display.V1.FormattedValue'] || audit.userid?.fullname || placeholder;
    const resolveResourceName = (resourceId?: string): string => {
        if (!resourceId || resourceId === '0') return placeholder;
        return getResource(resourceId)?.title || placeholder;
    };
    const resolveResourceImage = (resourceId?: string): string | undefined => {
        if (!resourceId || resourceId === '0') return undefined;
        return getResource(resourceId)?.profileImageUrl;
    };
    const parseChangeKeys = (changeData?: string): string[] => {
        if (!changeData) return [];
        const trimmed = changeData.trim();
        if (!trimmed) return [];
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                const parsed = JSON.parse(trimmed);
                if (parsed && typeof parsed === 'object') {
                    return Object.keys(parsed as Record<string, unknown>);
                }
            } catch {
                // Ignore parse errors; audit change payload formats can vary by environment.
            }
        }
        const tokens = trimmed.match(/[_a-zA-Z0-9]+/g) ?? [];
        const filtered = tokens.filter((token) =>
            token.includes('resource') || token.includes('bookingstatus') || token.includes('systemstatus') || token.includes('preferred'));
        return Array.from(new Set(filtered));
    };
    const findResourceIdFromChangeData = (changeData?: string): string | undefined => {
        if (!changeData || !/resource/i.test(changeData)) return undefined;
        const match = changeData.match(guidPattern);
        return match ? match[0] : undefined;
    };
    const getChangeNote = (audit: IAuditRecord): string | undefined => {
        const changeKeys = parseChangeKeys(audit.changedata);
        const labels = Array.from(new Set(changeKeys.map((key) => interestingFieldLabels[key]).filter(Boolean)));
        if (labels.length === 0) return undefined;
        return `Changed: ${labels.join(', ')}`;
    };
    const buildHistoryItems = (audits: IAuditRecordWithEntity[]): IHistoryTimelineItem[] => {
        if (!event?.id) return [];
        const items = audits.map((audit, index) => {
            const actionLabel = getAuditActionLabel(audit);
            const actorName = getAuditActorName(audit);
            const actorInitials = actorName === placeholder ? placeholder : getInitials(actorName);
            const auditDate = audit.createdon ? new Date(audit.createdon) : (event.start ?? new Date());
            const resourceIdFromChange = findResourceIdFromChangeData(audit.changedata);
            const resourceIdForAudit = resourceIdFromChange || event.resourceId;
            const assigneeName = resolveResourceName(resourceIdForAudit);
            const assigneeImageUrl = resolveResourceImage(resourceIdForAudit);

            let actionText = actorName === placeholder ? placeholder : `${actorName} updated the shift`;
            if (audit.entityType === 'booking') {
                if (actionLabel === 'Create') {
                    actionText = actorName === placeholder ? placeholder : `${actorName} assigned the shift to ${assigneeName}`;
                } else if (actionLabel === 'Assign') {
                    actionText = actorName === placeholder ? placeholder : `${actorName} reassigned the shift to ${assigneeName}`;
                } else if (actionLabel === 'Delete' || actionLabel === 'Cancel') {
                    actionText = actorName === placeholder ? placeholder : `${actorName} removed the assignment`;
                } else {
                    actionText = actorName === placeholder ? placeholder : `${actorName} updated the assignment`;
                }
            } else {
                if (actionLabel === 'Create') {
                    actionText = actorName === placeholder ? placeholder : `${actorName} created the shift`;
                } else if (actionLabel === 'Close') {
                    actionText = actorName === placeholder ? placeholder : `${actorName} closed the shift`;
                } else if (actionLabel === 'Cancel') {
                    actionText = actorName === placeholder ? placeholder : `${actorName} cancelled the shift`;
                }
            }

            return {
                key: audit.auditid || `history-${index}`,
                date: auditDate,
                dateLabel: format(auditDate, 'MMM d, yyyy'),
                weekdayLabel: format(auditDate, 'EEEE'),
                actorName,
                actorInitials,
                actorImageUrl: audit.entityType === 'booking' ? assigneeImageUrl : undefined,
                action: actionText,
                note: getChangeNote(audit),
            } as IHistoryTimelineItem;
        });
        return items.sort((a, b) => b.date.getTime() - a.date.getTime());
    };
    const fetchAuditsForObject = async (objectId: string, entityType: 'workorder' | 'booking'): Promise<IAuditRecordWithEntity[]> => {
        if (!objectId) return [];
        const response = await get('/Entity/audits', {
            select: 'auditid,action,createdon,changedata,attributemask,_objectid_value,_userid_value',
            filter: `_objectid_value eq '${objectId}'`,
            expand: 'userid($select=fullname)',
            orderby: 'createdon desc',
            top: 50,
        }) as IAuditResponse;
        const audits = response?.value ?? [];
        return audits.map((audit) => ({ ...audit, entityType }));
    };
    useEffect(() => {
        if (!open || activeTab !== 'notes' || !event?.id) return;
        let isCancelled = false;
        const loadHistory = async () => {
            setHistoryLoading(true);
            setHistoryError(null);
            try {
                let bookingId = event.BookingId;
                if (!bookingId) {
                    const refreshed = await fetchBookingByJobId(event.id);
                    bookingId = refreshed.BookingId;
                }
                const workorderAuditsPromise = fetchAuditsForObject(event.id, 'workorder');
                const bookingAuditsPromise = bookingId ? fetchAuditsForObject(bookingId, 'booking') : Promise.resolve([]);
                const [workorderAudits, bookingAudits] = await Promise.all([workorderAuditsPromise, bookingAuditsPromise]);
                const nextHistoryItems = buildHistoryItems([...workorderAudits, ...bookingAudits]);
                if (!isCancelled) {
                    setHistoryItems(nextHistoryItems);
                }
            } catch (error) {
                if (!isCancelled) {
                    setHistoryItems([]);
                    setHistoryError('****');
                }
            } finally {
                if (!isCancelled) {
                    setHistoryLoading(false);
                }
            }
        };
        loadHistory();
        return () => {
            isCancelled = true;
        };
    }, [activeTab, open, event?.id, event?.BookingId, event?.resourceId]);
    const preferredStaffList: IResource[] = [];
    const computeMatchPercent = (resource: IResource): number | null => {
        if (requiredSkillsLower.length === 0) return null;
        const resourceSkills = resource.characteristics.map((char) => char.name.toLowerCase());
        const matched = requiredSkillsLower.filter((skill) => resourceSkills.includes(skill)).length;
        return Math.round((matched / requiredSkillsLower.length) * 100);
    };
    const candidateStaff = (allResources ?? []).filter(
        (resource) => resource?.id && resource.id !== '0' && isUserBackedResource(resource.id)
    );
    const shouldSurfaceAiAllocations = isUnassigned || aiAllocationsMode === 'manual' || activeTab === 'staff';
    const activeAllocations = shouldSurfaceAiAllocations ? aiAllocations : [];
    const aiMatches = (() => {
        const bestByResource = new Map<string, IAiAllocation>();
        activeAllocations.forEach((allocation) => {
            if (!allocation?.resourceId) return;
            const existing = bestByResource.get(allocation.resourceId);
            if (!existing || allocation.matchPercent > existing.matchPercent) {
                bestByResource.set(allocation.resourceId, allocation);
            }
        });
        return Array.from(bestByResource.values())
            .map((allocation) => {
                const resource = candidateStaff.find((r) => r.id === allocation.resourceId);
                return resource ? { allocation, resource } : null;
            })
            .filter((match): match is { allocation: IAiAllocation; resource: IResource } => match !== null)
            .sort((a, b) => b.allocation.matchPercent - a.allocation.matchPercent);
    })();
    const topMatches = aiMatches.slice(0, 3);
    const otherMatches = aiMatches.slice(3);
    const cachedAllocations = event?.id ? (aiSuggestionsByShiftRef.current[event.id] ?? []) : [];
    const cachedMatches = cachedAllocations
        .map((allocation) => {
            const resource = candidateStaff.find((r) => r.id === allocation.resourceId);
            return resource ? { allocation, resource } : null;
        })
        .filter((match): match is { allocation: IAiAllocation; resource: IResource } => match !== null)
        .sort((a, b) => b.allocation.matchPercent - a.allocation.matchPercent);
    const showCachedMatches = cachedMatches.length > 0 && activeAllocations.length === 0;
    const hasActiveAllocations = activeAllocations.length > 0;
    const findBestMatchButtonLabel = hasActiveAllocations ? 'Re Run Find Best Match' : 'Find Best Match';
    const hasShiftBiddingStarted = !!event?.isJobBidding || hasPersistedBiddingUnassignedMarker;
    const startShiftBiddingButtonLabel = hasShiftBiddingStarted
        ? 'Shift Bidding Started'
        : (startingShiftBidding ? 'Starting...' : 'Start Shift Bidding');
    const startShiftBiddingButtonClassName = [
        'event-modal-rounded-button',
        'start-shift-bidding-button',
        hasCompletedOptimisationForShift && !hasShiftBiddingStarted ? 'start-shift-bidding-button--ready' : '',
        hasShiftBiddingStarted ? 'start-shift-bidding-button--started' : '',
    ].filter(Boolean).join(' ');
    const disableStartShiftBidding = (
        !event
        || hasShiftBiddingStarted
        || !hasCompletedOptimisationForShift
        || startingShiftBidding
        || awaitingPublish
        || awaitingSave
        || autoInviting
        || !!invitingResourceId
    );
    const aiStatusClassName = [
        'staff-ai-status',
        aiStatusMessage ? 'staff-ai-status--banner' : '',
        isAiStatusWarningMessage(aiStatusMessage) ? 'staff-ai-status--warning' : ''
    ].filter(Boolean).join(' ');
    const isActiveInvitationStatus = (status: unknown) => {
        const normalized = String(status ?? '').trim().toLocaleLowerCase();
        return normalized === 'invited' || normalized === 'expressed interest';
    };
    const invitedResourceIds = useMemo(() => {
        const ids = new Set<string>();
        (event?.bidders ?? []).forEach((bidder) => {
            if (!isActiveInvitationStatus((bidder as any)?.bookingStatus)) return;
            const bidderId = String((bidder as any)?.bookableResourceId ?? '').trim();
            if (bidderId) ids.add(bidderId);
        });
        return ids;
    }, [event?.bidders]);
    const invitedResources = useMemo(() => {
        if (!invitedResourceIds.size) return [] as IResource[];
        return candidateStaff.filter((resource) => invitedResourceIds.has(resource.id));
    }, [candidateStaff, invitedResourceIds]);
    const currentInvitationStatuses = useMemo<IInvitationStatusRow[]>(() => {
        const shiftId = String(event?.id ?? '').trim();
        const historicalStatuses = shiftId
            ? (invitationStatusHistoryByShiftRef.current[shiftId] ?? [])
            : [];
        const statusesByResource = new Map<string, IInvitationStatusRow>();

        historicalStatuses.forEach((invitee) => {
            statusesByResource.set(invitee.resourceId, {
                ...invitee,
                assignmentLabel: null
            });
        });

        (event?.bidders ?? [])
            .map((bidder: any) => {
                const resourceId = String(bidder?.bookableResourceId ?? '').trim();
                if (!resourceId) return null;
                const resource = candidateStaff.find((item) => item.id === resourceId) ?? getResource(resourceId);
                const bookingStatus = normalizeBookingStatus(bidder?.bookingStatus);
                const invitationSeenAt = String((bidder as any)?.invitationSeenAt ?? '').trim();
                const invitationOpenedAt = String((bidder as any)?.invitationOpenedAt ?? '').trim();
                const displayStatus = bookingStatus === 'invited'
                    ? (invitationOpenedAt && !Number.isNaN(Date.parse(invitationOpenedAt))
                        ? 'opened'
                        : invitationSeenAt && !Number.isNaN(Date.parse(invitationSeenAt))
                            ? 'viewed'
                            : 'invited')
                    : bookingStatus;
                return {
                    resourceId,
                    resourceName: resource?.title ?? 'Resource',
                    resource: resource ?? null,
                    status: displayStatus,
                    statusLabel: formatBookingStatusLabel(displayStatus),
                    assignmentLabel: null
                } as IInvitationStatusRow;
            })
            .filter((invitee): invitee is IInvitationStatusRow => invitee !== null)
            .forEach((invitee) => {
                const existing = statusesByResource.get(invitee.resourceId);
                statusesByResource.set(invitee.resourceId, {
                    ...existing,
                    ...invitee,
                    resource: invitee.resource ?? existing?.resource ?? null,
                    assignmentLabel: existing?.assignmentLabel ?? null
                });
            });

        const assignedResourceId = String(event?.resourceId ?? '').trim();
        const hasInvitationHistory = statusesByResource.size > 0;
        if (assignedResourceId && assignedResourceId !== '0' && hasInvitationHistory) {
            const resource = candidateStaff.find((item) => item.id === assignedResourceId) ?? getResource(assignedResourceId);
            const existingAssignedInvitee = statusesByResource.get(assignedResourceId);
            statusesByResource.set(assignedResourceId, {
                resourceId: assignedResourceId,
                resourceName: resource?.title ?? existingAssignedInvitee?.resourceName ?? 'Resource',
                resource: resource ?? existingAssignedInvitee?.resource ?? null,
                status: existingAssignedInvitee?.status ?? 'accepted',
                statusLabel: existingAssignedInvitee?.statusLabel ?? 'Accepted',
                assignmentLabel: 'Assigned'
            });
        }

        return Array.from(statusesByResource.values())
            .sort((a, b) => a.resourceName.localeCompare(b.resourceName));
    }, [candidateStaff, event?.bidders, event?.id, event?.resourceId, getResource]);
    const assignedShiftBiddingInvitee = useMemo<IInvitationStatusRow | null>(() => {
        const assignedResourceId = String(event?.resourceId ?? '').trim();
        if (!assignedResourceId || assignedResourceId === '0') return null;
        return currentInvitationStatuses.find((invitee) => invitee.resourceId === assignedResourceId) ?? null;
    }, [currentInvitationStatuses, event?.resourceId]);
    const hasActiveInvitationStatuses = useMemo(
        () => currentInvitationStatuses.some((invitee) => isActiveInvitationStatus(invitee.status)),
        [currentInvitationStatuses]
    );
    useEffect(() => {
        const shiftId = String(event?.id ?? '').trim();
        if (!shiftId || currentInvitationStatuses.length < 1) return;
        invitationStatusHistoryByShiftRef.current[shiftId] = currentInvitationStatuses.map((invitee) => ({
            ...invitee,
            assignmentLabel: null
        }));
    }, [currentInvitationStatuses, event?.id]);
    useEffect(() => {
        if (!open || !aiStatusMessage || !isInvitationDeliveryStatusMessage(aiStatusMessage)) return;
        if (assignedShiftBiddingInvitee || !hasActiveInvitationStatuses) {
            setAiStatusMessage(null);
        }
    }, [open, aiStatusMessage, assignedShiftBiddingInvitee, hasActiveInvitationStatuses]);
    const suggestedInviteMatches = useMemo(() => {
        if (topMatches.length > 0 || otherMatches.length > 0) {
            return [...topMatches, ...otherMatches];
        }
        return showCachedMatches ? cachedMatches : [];
    }, [topMatches, otherMatches, showCachedMatches, cachedMatches]);
    const inviteChecklistResourceMap = useMemo(() => {
        const resourceMap = new Map<string, { resource: IResource; allocation?: IAiAllocation }>();
        suggestedInviteMatches.forEach(({ resource, allocation }) => {
            if (!resource?.id || invitedResourceIds.has(resource.id)) return;
            resourceMap.set(resource.id, { resource, allocation });
        });
        manualInviteResourceIds.forEach((resourceId) => {
            const normalizedResourceId = String(resourceId ?? '').trim();
            if (!normalizedResourceId || invitedResourceIds.has(normalizedResourceId) || resourceMap.has(normalizedResourceId)) return;
            const resource = candidateStaff.find((item) => item.id === normalizedResourceId) ?? getResource(normalizedResourceId);
            if (!resource) return;
            resourceMap.set(normalizedResourceId, { resource });
        });
        return resourceMap;
    }, [candidateStaff, getResource, invitedResourceIds, manualInviteResourceIds, suggestedInviteMatches]);
    const inviteChecklistEntries = useMemo(
        () => Array.from(inviteChecklistResourceMap.values()),
        [inviteChecklistResourceMap]
    );
    const visibleStaffDistanceResources = useMemo(() => {
        const byId = new Map<string, IResource>();

        [...aiMatches, ...cachedMatches].forEach(({ resource }) => {
            if (resource?.id) {
                byId.set(resource.id, resource);
            }
        });

        inviteChecklistEntries.forEach(({ resource }) => {
            if (resource?.id) {
                byId.set(resource.id, resource);
            }
        });

        return Array.from(byId.values());
    }, [aiMatches, cachedMatches, inviteChecklistEntries]);
    const staffDistanceRequestKey = useMemo(() => {
        if (!open || activeTab !== 'staff' || !event) {
            return '';
        }

        const shiftSiteId = String(event.siteLocationDetails?.id ?? '').trim();
        const shiftSiteLatitude = toFiniteCoordinateValue(event.siteLocationDetails?.latitude) ?? toFiniteCoordinateValue(siteLocationCoordinates?.latitude);
        const shiftSiteLongitude = toFiniteCoordinateValue(event.siteLocationDetails?.longitude) ?? toFiniteCoordinateValue(siteLocationCoordinates?.longitude);

        const resourceKey = visibleStaffDistanceResources
            .map((resource) => {
                const matchingSite = (resource.siteLocation ?? []).find((location) =>
                    String(location.illumina_sitelocationid ?? '').trim() === shiftSiteId
                );

                return [
                    String(resource.id ?? '').trim(),
                    String(resource.startLocationType ?? ''),
                    String(resource.startLocationLabel ?? '').trim(),
                    toFiniteCoordinateValue(resource.resourceAddressCoordinates?.latitude) ?? '',
                    toFiniteCoordinateValue(resource.resourceAddressCoordinates?.longitude) ?? '',
                    matchingSite ? String(matchingSite.illumina_sitelocationid ?? '').trim() : '',
                    matchingSite ? (toFiniteCoordinateValue(matchingSite.illumina_latitudes) ?? '') : '',
                    matchingSite ? (toFiniteCoordinateValue(matchingSite.illumina_longitudes) ?? '') : ''
                ].join(':');
            })
            .sort()
            .join('|');

        return [
            String(event.id ?? '').trim(),
            shiftSiteId,
            shiftSiteLatitude ?? '',
            shiftSiteLongitude ?? '',
            resourceKey
        ].join('||');
    }, [activeTab, event, open, siteLocationCoordinates, visibleStaffDistanceResources]);
    const inviteChecklistEntriesById = useMemo(() => {
        const entries = new Map<string, { resource: IResource; allocation?: IAiAllocation }>();
        inviteChecklistEntries.forEach((entry) => {
            entries.set(entry.resource.id, entry);
        });
        return entries;
    }, [inviteChecklistEntries]);
    const inviteChecklistResourceIds = useMemo(
        () => inviteChecklistEntries.map((entry) => entry.resource.id),
        [inviteChecklistEntries]
    );
    const selectedInviteResources = useMemo(
        () => inviteChecklistEntries
            .filter((entry) => inviteSelectionByResourceId[entry.resource.id])
            .map((entry) => entry.resource),
        [inviteChecklistEntries, inviteSelectionByResourceId]
    );
    const inviteChecklistResourceIdsKey = useMemo(
        () => inviteChecklistResourceIds.join('|'),
        [inviteChecklistResourceIds]
    );
    const invitedResourceIdsKey = useMemo(
        () => Array.from(invitedResourceIds).sort().join('|'),
        [invitedResourceIds]
    );
    const manualInviteEligibleResources = useMemo(
        () => (allResources ?? [])
            .filter((resource) => resource?.id && resource.id !== '0')
            .sort((a, b) => String(a.title ?? '').localeCompare(String(b.title ?? ''))),
        [allResources]
    );
    const manualInviteSearchQuery = useMemo(
        () => manualInviteSearchText.trim().toLocaleLowerCase(),
        [manualInviteSearchText]
    );
    const manualInviteOptions = useMemo(() => {
        const inChecklistIds = new Set(inviteChecklistResourceIds);
        return manualInviteEligibleResources
            .filter((resource) => !invitedResourceIds.has(resource.id))
            .filter((resource) => {
                if (!manualInviteSearchQuery) {
                    return true;
                }

                const resourceName = String(resource.title ?? '').toLocaleLowerCase();
                const resourceId = String(resource.id ?? '').toLocaleLowerCase();
                return resourceName.includes(manualInviteSearchQuery) || resourceId.includes(manualInviteSearchQuery);
            })
            .map((resource) => ({
                key: resource.id,
                text: resource.title || resource.id,
                disabled: inChecklistIds.has(resource.id) && !manualInviteResourceIds.includes(resource.id)
            }))
            .sort((left, right) => {
                const leftText = String(left.text ?? '').toLocaleLowerCase();
                const rightText = String(right.text ?? '').toLocaleLowerCase();
                const leftStartsWith = leftText.startsWith(manualInviteSearchQuery);
                const rightStartsWith = rightText.startsWith(manualInviteSearchQuery);
                if (leftStartsWith !== rightStartsWith) {
                    return leftStartsWith ? -1 : 1;
                }
                return leftText.localeCompare(rightText);
            })
            .slice(0, 12);
    }, [inviteChecklistResourceIds, invitedResourceIds, manualInviteEligibleResources, manualInviteResourceIds, manualInviteSearchQuery]);
    useEffect(() => {
        const comboBox = manualInviteComboBoxRef.current;
        if (!comboBox || !manualInviteSearchQuery) {
            return;
        }

        if (manualInviteOptions.length > 0) {
            comboBox.focus(true);
            return;
        }

        comboBox.dismissMenu();
    }, [manualInviteOptions.length, manualInviteSearchQuery]);
    useEffect(() => {
        let active = true;

        const loadStaffDistances = async () => {
            if (!staffDistanceRequestKey || !event || !open || activeTab !== 'staff') {
                if (active) {
                    setStaffDistanceByResourceId({});
                }
                return;
            }

            const resourcesToMeasure = visibleStaffDistanceResources.filter((resource) =>
                resource?.id &&
                resource.id !== '0'
            );
            const shiftSiteUnavailableReason = getShiftSiteDistanceUnavailableReason(event, siteLocationAddress);
            const shiftCoordinate = toCoordinateOrNull(event.siteLocationDetails) ?? toCoordinateOrNull(siteLocationCoordinates);
            if (!shiftCoordinate) {
                if (active) {
                    setStaffDistanceByResourceId(
                        resourcesToMeasure.reduce<Record<string, StaffDistanceState>>((acc, resource) => {
                            acc[resource.id] = toUnavailableDistanceState(shiftSiteUnavailableReason);
                            return acc;
                        }, {})
                    );
                }
                return;
            }

            if (resourcesToMeasure.length < 1) {
                if (active) {
                    setStaffDistanceByResourceId({});
                }
                return;
            }

            const immediateDistances: Record<string, StaffDistanceState> = {};
            const requestSegments: Array<{
                id: string;
                resourceId: string;
                from: {
                    latitude: number;
                    longitude: number;
                };
                to: {
                    latitude: number;
                    longitude: number;
                };
            }> = [];

            resourcesToMeasure.forEach((resource) => {
                const matchingResourceSite = (resource.siteLocation ?? []).find((location) =>
                    String(location.illumina_sitelocationid ?? '').trim() === String(event.siteLocationDetails?.id ?? '').trim()
                );
                const organizationalUnitFallbackCoordinate = matchingResourceSite
                    ? {
                        latitude: toFiniteCoordinateValue(matchingResourceSite.illumina_latitudes),
                        longitude: toFiniteCoordinateValue(matchingResourceSite.illumina_longitudes)
                    }
                    : shiftCoordinate;

                const startAnchor = resolveResourceTravelAnchor(
                    resource,
                    resource.startLocationType,
                    resource.startLocationLabel,
                    shiftCoordinate,
                    organizationalUnitFallbackCoordinate
                );

                if (!startAnchor) {
                    immediateDistances[resource.id] = toUnavailableDistanceState(
                        getResourceDistanceUnavailableReason(resource, shiftSiteUnavailableReason)
                    );
                    return;
                }

                if (coordinatesEqual(startAnchor, shiftCoordinate)) {
                    immediateDistances[resource.id] = {
                        distanceLabel: '0.0 km',
                        durationLabel: '0m',
                        tooltip: 'The resource start location and the shift site location use the same coordinates.'
                    };
                    return;
                }

                requestSegments.push({
                    id: `staff-distance:${event.id}:${resource.id}`,
                    resourceId: resource.id,
                    from: startAnchor,
                    to: shiftCoordinate
                });
            });

            if (active) {
                setStaffDistanceByResourceId(immediateDistances);
            }

            if (requestSegments.length < 1) {
                return;
            }

            try {
                const payload = await postOrThrow('/Travel/measure', {
                    segments: requestSegments.map((segment) => ({
                        id: segment.id,
                        from: segment.from,
                        to: segment.to
                    }))
                }) as ITravelMeasureResult;

                if (!active) {
                    return;
                }

                const segmentById = new Map(requestSegments.map((segment) => [segment.id, segment]));
                const nextDistances = { ...immediateDistances };

                (payload?.segments ?? []).forEach((measurement: ITravelMeasureResultSegment) => {
                    const sourceSegment = segmentById.get(String(measurement.id ?? ''));
                    if (!sourceSegment) {
                        return;
                    }

                    if (measurement.status !== 'ok') {
                        nextDistances[sourceSegment.resourceId] = toUnavailableDistanceState(
                            getMeasurementUnavailableReason(measurement.status)
                        );
                        return;
                    }

                    const availableDistanceState = toAvailableDistanceState(
                        measurement.distanceMeters,
                        measurement.durationSeconds
                    );
                    if (!availableDistanceState) {
                        nextDistances[sourceSegment.resourceId] = toUnavailableDistanceState(
                            'OSRM returned a response, but the distance values were incomplete.'
                        );
                        return;
                    }

                    nextDistances[sourceSegment.resourceId] = availableDistanceState;
                });

                requestSegments.forEach((segment) => {
                    if (!nextDistances[segment.resourceId]) {
                        nextDistances[segment.resourceId] = toUnavailableDistanceState(
                            'OSRM did not return a distance for this resource.'
                        );
                    }
                });

                setStaffDistanceByResourceId(nextDistances);
            } catch (error) {
                if (!active) {
                    return;
                }

                console.error('Failed to load event modal staff distances.', error);
                const failedDistances = { ...immediateDistances };
                requestSegments.forEach((segment) => {
                    failedDistances[segment.resourceId] = toUnavailableDistanceState(
                        'The OSRM distance request failed for this resource. Please try again.'
                    );
                });
                setStaffDistanceByResourceId(failedDistances);
            }
        };

        loadStaffDistances();

        return () => {
            active = false;
        };
    }, [postOrThrow, staffDistanceRequestKey]);
    const aiRules = [
        `Uses bookings, leaves, and availability windows to check hard feasibility.`,
        `Chooses the top ${MAX_SUGGESTED_RESOURCES} matches per unassigned job when possible.`,
        "Never repeats the same resource for the same job (no duplicates).",
        "Prefers skill compliance, travel feasibility, contract and preferred hours, and cost balance.",
        "Treats skill validity as part of the skill compliance goal.",
        "Returns a concise reason for each match based on the data.",
        "Matching Score is 0-100 and must reflect evidence in the payload."
    ];
    const getInviteErrorMessage = (error: unknown) => {
        const candidate = error as any;
        const data = candidate?.response?.data;
        if (typeof data === 'string' && data.trim().length > 0) {
            return data.trim();
        }
        if (data && typeof data === 'object') {
            const detail = data.detail ?? data.title ?? data.message ?? data.error;
            if (typeof detail === 'string' && detail.trim().length > 0) {
                return detail.trim();
            }
            if (Array.isArray(data.errors) && data.errors.length > 0) {
                const firstError = data.errors[0];
                if (typeof firstError === 'string' && firstError.trim().length > 0) {
                    return firstError.trim();
                }
            }
        }
        if (typeof candidate?.message === 'string' && candidate.message.trim().length > 0) {
            return candidate.message.trim();
        }
        return 'Unknown error.';
    };
    const formatPushSubscriptionWarning = (warning: string | null) => {
        const normalizedWarning = toNonEmptyString(warning);
        if (!normalizedWarning) return null;
        const subscriptionMatch = /^No active push subscriptions found for (\d+) invitation target\(s\)\.?$/i.exec(normalizedWarning);
        if (!subscriptionMatch) return normalizedWarning;

        const affectedCount = Number(subscriptionMatch[1] ?? 0);
        if (!Number.isFinite(affectedCount) || affectedCount < 1) {
            return 'Some invited resources do not have MobilePlus push notifications enabled yet. They can still see the invitation in Notifications.';
        }

        return affectedCount === 1
            ? '1 invited resource does not have MobilePlus push notifications enabled yet. They can still see the invitation in Notifications.'
            : `${affectedCount} invited resources do not have MobilePlus push notifications enabled yet. They can still see the invitation in Notifications.`;
    };
    function isInvitationDeliveryStatusMessage(message: string | null | undefined) {
        const normalizedMessage = String(message ?? '').trim().toLocaleLowerCase();
        if (!normalizedMessage) return false;
        return normalizedMessage.startsWith('invitation sent')
            || normalizedMessage.startsWith('invitations sent')
            || normalizedMessage.includes('mobileplus push notifications enabled')
            || normalizedMessage.includes('missing invitation booking ids');
    }
    function isAiStatusWarningMessage(message: string | null | undefined) {
        const normalizedMessage = String(message ?? '').trim().toLocaleLowerCase();
        if (!normalizedMessage) return false;
        return normalizedMessage.includes('failed')
            || normalizedMessage.includes('missing invitation booking ids')
            || normalizedMessage.includes('mobileplus push notifications enabled');
    }
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const inviteResourceWithRetry = async (resourceId: string, attempts = 3) => {
        let lastError: unknown = null;
        for (let attempt = 1; attempt <= attempts; attempt += 1) {
            try {
                logBiddingInfo('inviteResourceWithRetry:request', {
                    eventId: event?.id,
                    resourceId,
                    attempt,
                    attempts
                });
                const response = await postOrThrow(`/Rostering/bookableresource/${resourceId}/bidding/${event?.id}/invited`, {});
                const responseSummary = summarizeInviteResponse(response);
                logBiddingInfo('inviteResourceWithRetry:success', {
                    eventId: event?.id,
                    requestedResourceId: resourceId,
                    attempt,
                    ...responseSummary
                });
                logBiddingDebug('inviteResourceWithRetry:response', {
                    eventId: event?.id,
                    resourceId,
                    attempt,
                    response
                });
                if (!isGuidLike(responseSummary.bookingId)) {
                    logBiddingDebug('inviteResourceWithRetry:booking-id-missing', {
                        eventId: event?.id,
                        resourceId,
                        attempt,
                        bookingId: responseSummary.bookingId
                    });
                }
                logBiddingDebug('inviteResourceWithRetry:booking-id-present', {
                    eventId: event?.id,
                    resourceId,
                    attempt,
                    bookingId: responseSummary.bookingId
                });
                return {
                    response,
                    summary: responseSummary
                };
            } catch (error) {
                lastError = error;
                const shouldRetry = attempt < attempts;
                const errorMessage = getInviteErrorMessage(error);
                logBiddingInfo('inviteResourceWithRetry:error', {
                    eventId: event?.id,
                    resourceId,
                    attempt,
                    attempts,
                    status: (error as any)?.response?.status,
                    error: errorMessage,
                    willRetry: shouldRetry
                });
                if (!shouldRetry) break;
                await delay(300 * attempt);
            }
        }
        throw lastError;
    };
    const mergeInvitedBidders = (resourceIds: string[]) => {
        if (!event || resourceIds.length === 0) return;
        const existingByResource = new Map<string, any>();
        (event.bidders ?? []).forEach((bidder) => {
            const bidderId = String((bidder as any)?.bookableResourceId ?? '').trim();
            if (!bidderId) return;
            existingByResource.set(bidderId, bidder);
        });
        resourceIds
            .map((resourceId) => String(resourceId ?? '').trim())
            .filter((resourceId) => resourceId.length > 0)
            .forEach((resourceId) => {
                const existing = existingByResource.get(resourceId);
                if (existing) {
                    existingByResource.set(resourceId, {
                        ...existing,
                        bookingStatus: 'invited'
                    });
                    return;
                }
                existingByResource.set(resourceId, {
                    bookingStatus: 'invited',
                    id: `invited_${event.id}_${resourceId}`,
                    bookableResourceId: resourceId,
                    bookableResource: null,
                    invitationSeenAt: null,
                    invitationOpenedAt: null,
                    isSeen: false,
                    isOpened: false,
                    errors: [],
                    isOverTime: false,
                    missingSkills: false,
                    unavailable: false,
                    exceededMaxWorkingHours: false
                });
            });

        const nextBidders = Array.from(existingByResource.values());
        if (!nextBidders.length) return;
        setEvent((prev) => prev ? { ...prev, bidders: nextBidders as any } : prev);
        updateShiftInfo(event.id, { bidders: nextBidders as any });
    };
    const inviteResources = async (resources: IResource[], source: 'manual' | 'batch' = 'manual') => {
        if (!event?.id) return;
        logBiddingInfo('inviteResources:start', {
            eventId: event.id,
            source,
            requestedResources: resources.map((r) => r.id)
        });
        logBiddingDebug('inviteResources:start', {
            eventId: event.id,
            source,
            resources: resources.map((r) => r.id),
            isUnassigned,
            isJobBidding: event.isJobBidding,
            invitedResourceIds: Array.from(invitedResourceIds)
        });
        if (!event.isJobBidding) {
            toast('warning', 'Enable Open for Bidding before sending invitations.');
            logBiddingDebug('inviteResources:blocked-not-open-for-bidding', { eventId: event.id });
            return;
        }
        if (!isUnassigned) {
            toast('info', 'Shift is already assigned.');
            logBiddingDebug('inviteResources:blocked-assigned', { eventId: event.id, resourceId: event.resourceId });
            return;
        }

        const uniqueResources = resources.filter((resource, index) =>
            resource?.id && resources.findIndex((candidate) => candidate.id === resource.id) === index
        );
        const pending = uniqueResources.filter((resource) => !invitedResourceIds.has(resource.id));
        logBiddingInfo('inviteResources:prepared', {
            eventId: event.id,
            source,
            uniqueResourceIds: uniqueResources.map((resource) => resource.id),
            pendingResourceIds: pending.map((resource) => resource.id),
            alreadyInvitedResourceIds: uniqueResources
                .filter((resource) => invitedResourceIds.has(resource.id))
                .map((resource) => resource.id)
        });
        if (!pending.length) {
            if (source === 'manual') {
                toast('info', 'Resource is already invited.');
            }
            logBiddingDebug('inviteResources:no-pending', { eventId: event.id });
            return;
        }

        const invitedIds: string[] = [];
        const invitedNotificationItems: Array<{ resourceId: string; summary: ReturnType<typeof summarizeInviteResponse> }> = [];
        let firstInviteErrorMessage: string | null = null;
        setAutoInviting(true);

        try {
            for (const resource of pending) {
                setInvitingResourceId(resource.id);
                try {
                    const inviteResponse = await inviteResourceWithRetry(resource.id);
                    invitedIds.push(resource.id);
                    invitedNotificationItems.push({
                        resourceId: resource.id,
                        summary: inviteResponse.summary
                    });
                    logBiddingInfo('inviteResources:invited', {
                        eventId: event.id,
                        source,
                        resourceId: resource.id,
                        invite: inviteResponse.summary
                    });
                    logBiddingDebug('inviteResources:invited', { eventId: event.id, resourceId: resource.id });
                } catch (error) {
                    const errorMessage = getInviteErrorMessage(error);
                    if (!firstInviteErrorMessage) {
                        firstInviteErrorMessage = errorMessage;
                    }
                    logBiddingDebug('inviteResources:error', {
                        eventId: event.id,
                        resourceId: resource.id,
                        error: errorMessage,
                        status: (error as any)?.response?.status
                    });
                }
            }
        } finally {
            setInvitingResourceId(null);
        }

        try {
            if (invitedIds.length > 0) {
                mergeInvitedBidders(invitedIds);
                logBiddingInfo('inviteResources:completed', {
                    eventId: event.id,
                    source,
                    invitedIds,
                    attemptedCount: pending.length,
                    failedCount: pending.length - invitedIds.length
                });
                logBiddingDebug('inviteResources:completed', { eventId: event.id, invitedIds });
                setInviteSelectionByResourceId((prev) => {
                    const next = { ...prev };
                    invitedIds.forEach((resourceId) => {
                        delete next[resourceId];
                    });
                    return next;
                });
                setManualInviteResourceIds((prev) => prev.filter((resourceId) => !invitedIds.includes(resourceId)));
                const notifyResult = await notifyInvitedResources(invitedNotificationItems);
                const notifyBody = notifyResult.body as any;
                const notifyWarning = formatPushSubscriptionWarning(notifyBody?.warning ?? notifyBody?.Warning);
                const notifyFailedCount = Number(notifyBody?.failed ?? notifyBody?.Failed ?? 0);
                const successMessage = invitedIds.length === 1
                    ? `Invitation sent to ${pending.find((resource) => invitedIds.includes(resource.id))?.title ?? 'resource'}.`
                    : `Invitations sent to ${invitedIds.length} resources.`;
                const blockingIssues: string[] = [];
                const followUpNotes: string[] = [];
                if (notifyResult.errorMessage) {
                    blockingIssues.push(`MobilePlus notify handoff failed. ${notifyResult.errorMessage}`);
                } else {
                    if (notifyResult.skippedResourceIds.length > 0) {
                        blockingIssues.push(`Missing invitation booking IDs for ${notifyResult.skippedResourceIds.length} resource(s).`);
                    }
                    if (notifyFailedCount > 0) {
                        blockingIssues.push(`Push delivery reported ${notifyFailedCount} failed target(s).`);
                    }
                    if (notifyWarning) {
                        followUpNotes.push(notifyWarning);
                    }
                }
                const userMessage = `${successMessage} ${[...blockingIssues, ...followUpNotes].join(' ')}`.trim();
                if (blockingIssues.length > 0) {
                    const warningMessage = userMessage;
                    setAiStatusMessage(warningMessage);
                    toast('warning', warningMessage);
                } else if (followUpNotes.length > 0) {
                    setAiStatusMessage(userMessage);
                    toast('success', userMessage);
                } else {
                    toast('success', successMessage);
                }
                return;
            }

            logBiddingInfo('inviteResources:failed', {
                eventId: event.id,
                source,
                attemptedResourceIds: pending.map((resource) => resource.id),
                error: firstInviteErrorMessage ?? 'Unknown error.'
            });
            logBiddingDebug('inviteResources:failed', { eventId: event.id });
            const baseMessage = pending.length > 1 ? 'Failed to send invitations.' : 'Failed to send invitation.';
            const failureMessage = firstInviteErrorMessage ? `${baseMessage} ${firstInviteErrorMessage}` : baseMessage;
            setAiStatusMessage(failureMessage);
            toast('error', failureMessage);
        } finally {
            setAutoInviting(false);
        }
    };
    const updateInviteSelections = (resourceIds: string[], checked: boolean) => {
        if (resourceIds.length < 1) return;
        setInviteSelectionByResourceId((prev) => {
            const next = { ...prev };
            resourceIds.forEach((resourceId) => {
                const normalizedResourceId = String(resourceId ?? '').trim();
                if (!normalizedResourceId) return;
                next[normalizedResourceId] = checked;
            });
            return next;
        });
    };
    const handleToggleInviteSelection = (resourceId: string, checked: boolean) => {
        updateInviteSelections([resourceId], checked);
    };
    const handleSelectAllInvites = () => {
        updateInviteSelections(inviteChecklistResourceIds, true);
    };
    const handleUnselectAllInvites = () => {
        updateInviteSelections(inviteChecklistResourceIds, false);
    };
    const handleSendSelectedInvites = async () => {
        if (selectedInviteResources.length < 1) {
            toast('info', 'Select at least one resource before sending invitations.');
            return;
        }
        await inviteResources(selectedInviteResources, selectedInviteResources.length > 1 ? 'batch' : 'manual');
    };
    const handleChangeManualInviteResources = (
        eventOrOption?: React.FormEvent<IComboBox>,
        option?: IComboBoxOption,
        _index?: number,
        value?: string
    ) => {
        const matchingManualInviteOption = (manualInviteOptions as IComboBoxOption[]).find((candidate) =>
            String(candidate.text ?? '').trim().toLocaleLowerCase() === String(value ?? '').trim().toLocaleLowerCase()
        );
        const selectedOption = option
            ?? ((eventOrOption as any)?.option as IComboBoxOption | undefined)
            ?? matchingManualInviteOption;
        const resourceId = String(selectedOption?.key ?? '').trim();
        if (!resourceId || invitedResourceIds.has(resourceId) || selectedOption?.disabled) return;

        const shouldInclude = !!selectedOption?.selected;
        setManualInviteResourceIds((prev) => shouldInclude
            ? Array.from(new Set([...prev, resourceId]))
            : prev.filter((id) => id !== resourceId)
        );
        setInviteSelectionByResourceId((prev) => {
            const next = { ...prev };
            if (shouldInclude) {
                next[resourceId] = true;
            } else {
                delete next[resourceId];
            }
            return next;
        });
        logBiddingDebug('inviteChecklist:toggle-manual-resource', {
            eventId: event?.id,
            resourceId,
            selected: shouldInclude
        });
    };
    useEffect(() => {
        if (!open || activeTab !== 'staff' || !event?.id || !event.isJobBidding || !isUnassigned) return;
        const hasHydratedLocalChecklist = Object.keys(inviteSelectionByResourceId).length > 0 || manualInviteResourceIds.length > 0;
        if (hasHydratedLocalChecklist) return;
        const draft = getInviteChecklistDraft(event.id);
        const draftSelectedIds = new Set(draft?.selectedResourceIds ?? []);
        const draftManualIds = (draft?.manualResourceIds ?? [])
            .map((resourceId) => String(resourceId ?? '').trim())
            .filter((resourceId) => resourceId.length > 0 && !invitedResourceIds.has(resourceId));
        const availableIds = Array.from(new Set([
            ...inviteChecklistEntries.map((entry) => entry.resource.id),
            ...draftManualIds
        ]));

        if (availableIds.length < 1) return;

        if (!draft) {
            const selectedResourceIds = invitedResourceIds.size > 0 ? [] : inviteChecklistEntries.map((entry) => entry.resource.id);
            const nextSelection = selectedResourceIds.reduce((acc, resourceId) => {
                acc[resourceId] = true;
                return acc;
            }, {} as Record<string, boolean>);
            setInviteSelectionByResourceId((prev) => areSelectionMapsEqual(prev, nextSelection) ? prev : nextSelection);
            setManualInviteResourceIds((prev) => areStringArraysEqual(prev, []) ? prev : []);
            setInviteChecklistDraft(event.id, {
                selectedResourceIds,
                manualResourceIds: []
            });
            logBiddingDebug('inviteChecklist:seeded', {
                eventId: event.id,
                selectedResourceIds,
                activeInviteCount: invitedResourceIds.size
            });
            return;
        }

        const nextSelection: Record<string, boolean> = {};
        availableIds.forEach((resourceId) => {
            nextSelection[resourceId] = draftSelectedIds.has(resourceId);
        });

        setInviteSelectionByResourceId((prev) => areSelectionMapsEqual(prev, nextSelection) ? prev : nextSelection);
        setManualInviteResourceIds((prev) => areStringArraysEqual(prev, draftManualIds) ? prev : draftManualIds);
    }, [
        activeTab,
        event?.id,
        event?.isJobBidding,
        inviteChecklistResourceIdsKey,
        invitedResourceIdsKey,
        inviteSelectionByResourceId,
        isUnassigned,
        manualInviteResourceIds.length,
        open
    ]);
    useEffect(() => {
        if (!event?.id) return;
        if (!event.isJobBidding || !isUnassigned) {
            removeInviteChecklistDraft(event.id, 'shift-left-invite-compose');
            return;
        }
        const selectedResourceIds = Object.entries(inviteSelectionByResourceId)
            .filter(([, isSelected]) => !!isSelected)
            .map(([resourceId]) => resourceId)
            .filter((resourceId) => inviteChecklistResourceMap.has(resourceId));
        setInviteChecklistDraft(event.id, {
            selectedResourceIds,
            manualResourceIds: manualInviteResourceIds
        });
    }, [event?.id, event?.isJobBidding, inviteChecklistResourceMap, inviteSelectionByResourceId, isUnassigned, manualInviteResourceIds]);
    const handleEditToggle = () => {
        if (activeTab !== 'details') {
            setActiveTab('details');
            setIsEditOpen(true);
            return;
        }
        setIsEditOpen((prev) => !prev);
    };
    const handleStartShiftBidding = async () => {
        if (!event) {
            logBiddingDebug('handleStartShiftBidding:skip', {
                reason: 'missing-shift'
            });
            return;
        }
        if (hasPersistedBiddingUnassignedMarker || event.isJobBidding) {
            logBiddingInfo('handleStartShiftBidding:skip', {
                eventId: event.id,
                reason: event.isJobBidding ? 'already-job-bidding' : 'persisted-unassigned-marker'
            });
            return;
        }
        if (startingShiftBidding) {
            logBiddingDebug('handleStartShiftBidding:skip', {
                eventId: event.id,
                reason: 'in-progress'
            });
            return;
        }
        if (!hasCompletedOptimisationForShift) {
            logBiddingInfo('handleStartShiftBidding:skip', {
                eventId: event.id,
                reason: 'optimisation-not-completed'
            });
            toast('info', 'Run Optimisations Suggestion before starting shift bidding.');
            return;
        }
        if (awaitingPublish || awaitingSave || autoInviting || !!invitingResourceId) {
            logBiddingInfo('handleStartShiftBidding:skip', {
                eventId: event.id,
                reason: 'flow-disabled',
                awaitingPublish,
                awaitingSave,
                autoInviting,
                invitingResourceId
            });
            return;
        }
        const shift = event;
        const latestPersistedMarker = resolvePersistedBiddingMarker(shift.id, 'start-flow-checkpoint');
        const shiftIsUnassigned = !shift.resourceId || shift.resourceId === '0';
        const requiresUnassignPublish = !!shift.BookingId && (
            !shiftIsUnassigned
            || shift.changeStatus !== ChangeStatus.UnChanged
        );
        const originalResourceId = normalizePersistedBiddingResourceId(shift.originalResourceID)
            || normalizePersistedBiddingResourceId(shift.resourceId)
            || '0';
        const hasPersistentMarkerBeforeStart = latestPersistedMarker !== null || hasPersistedBiddingUnassignedMarker;
        let markerWrittenByStartFlow = false;

        const confirmation = await Swal.fire({
            title: 'Start shift bidding?',
            text: 'By starting shift bidding, this job will become Unassigned until a candidate accepts or you manually assign staff.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: theme.palette.themePrimary,
            cancelButtonColor: theme.palette.tealLight,
            confirmButtonText: 'Start Shift Bidding',
            cancelButtonText: 'Cancel',
        });
        if (!confirmation.isConfirmed) {
            logBiddingDebug('handleStartShiftBidding:cancelled', {
                eventId: shift.id
            });
            return;
        }

        setStartingShiftBidding(true);
        try {
            let unassignedDuringStart = false;
            const previousBidders = shift.bidders ?? [];
            const preservedBidders = previousBidders.filter(
                (bidder) => !isActiveInvitationStatus((bidder as any)?.bookingStatus)
            );
            if (preservedBidders.length !== previousBidders.length) {
                setEvent((prev) => prev ? ({ ...prev, bidders: preservedBidders as any }) : prev);
                updateShiftInfo(shift.id, { bidders: preservedBidders as any });
                logBiddingInfo('handleStartShiftBidding:active-invites-reset', {
                    eventId: shift.id,
                    removedCount: previousBidders.length - preservedBidders.length
                });
            } else {
                logBiddingDebug('handleStartShiftBidding:active-invites-empty', {
                    eventId: shift.id
                });
            }

            lastAiRequestKeyRef.current = null;
            logBiddingInfo('handleStartShiftBidding:start', {
                eventId: shift.id,
                isUnassigned: shiftIsUnassigned,
                isJobBidding: shift.isJobBidding,
                persistedBiddingMarker: latestPersistedMarker !== null || hasPersistedBiddingUnassignedMarker,
                resourceId: shift.resourceId
            });
            logBiddingDebug('handleStartShiftBidding', {
                eventId: shift.id,
                isUnassigned: shiftIsUnassigned,
                isJobBidding: shift.isJobBidding,
                persistedBiddingMarker: latestPersistedMarker !== null || hasPersistedBiddingUnassignedMarker,
                resourceId: shift.resourceId
            });

            if (!shiftIsUnassigned) {
                const unassigned = await moveShift(shift.id, '0', false);
                if (!unassigned) {
                    toast('error', 'Unable to unassign shift before starting bidding.');
                    setSelectedResource(undefined);
                    logBiddingDebug('handleStartShiftBidding:unassign-failed', { eventId: shift.id });
                    logBiddingInfo('handleStartShiftBidding:unassign-failed', {
                        eventId: shift.id,
                        resourceId: shift.resourceId
                    });
                    return;
                }
                setEvent((prev) => prev ? ({
                    ...prev,
                    resourceId: '0'
                }) : prev);
                setSelectedResource(undefined);
                unassignedDuringStart = true;
                logBiddingInfo('handleStartShiftBidding:unassigned', { eventId: shift.id });
                logBiddingDebug('handleStartShiftBidding:unassigned', { eventId: shift.id });
            }

            if (requiresUnassignPublish) {
                setAwaitingPublish(true);
                try {
                    const bookingStatusList = await getBookingStatusList();
                    const cancelStatus = bookingStatusList.find((status) => {
                        const statusName = String(status?.name ?? '').trim().toLocaleLowerCase();
                        return statusName === 'canceled' || statusName === 'cancelled';
                    });
                    if (!cancelStatus?.bookingstatusid) {
                        toast('error', 'Unable to unassign shift before starting bidding.');
                        logBiddingInfo('handleStartShiftBidding:cancel-status-missing', {
                            eventId: shift.id
                        });
                        return;
                    }

                    logBiddingInfo('handleStartShiftBidding:cancel-booking:start', {
                        eventId: shift.id,
                        bookingId: shift.BookingId,
                        cancelStatusId: cancelStatus.bookingstatusid
                    });

                    await patch(`/Entity/bookableresourcebookings/${shift.BookingId}`, {
                        'BookingStatus@odata.bind': `/bookingstatuses(${cancelStatus.bookingstatusid})`,
                        duration: shift.shiftLength,
                        starttime: shift.start,
                        endtime: shift.end
                    });
                    await refreshBiddingSnapshotById(shift.id);

                    const refreshedShift = getShift(shift.id);
                    const persistedUnassigned = !refreshedShift?.resourceId || refreshedShift.resourceId === '0';
                    if (!refreshedShift || !persistedUnassigned) {
                        logBiddingInfo('handleStartShiftBidding:cancel-booking:failed', {
                            eventId: shift.id,
                            bookingId: shift.BookingId,
                            refreshedResourceId: refreshedShift?.resourceId ?? null
                        });
                        if (unassignedDuringStart && originalResourceId && originalResourceId !== '0') {
                            const restored = await moveShift(shift.id, originalResourceId, true);
                            if (restored) {
                                const restoredResource = getResource(originalResourceId);
                                setEvent((prev) => prev ? ({
                                    ...prev,
                                    resourceId: originalResourceId,
                                    isJobBidding: false
                                }) : prev);
                                setSelectedResource(restoredResource ?? undefined);
                            }
                        }
                        toast('error', 'Unable to unassign shift before starting bidding.');
                        return;
                    }

                    logBiddingInfo('handleStartShiftBidding:cancel-booking:success', {
                        eventId: shift.id
                    });
                    setEvent((prev) => prev ? ({
                        ...prev,
                        ...refreshedShift,
                        resourceId: '0',
                        originalResourceID: normalizePersistedBiddingResourceId(prev.originalResourceID) || originalResourceId,
                        changeStatus: ChangeStatus.UnChanged
                    }) : prev);
                    setSelectedResource(undefined);
                    if (unassignedDuringStart) {
                        toast('info', 'Shift unassigned and published. Starting shift bidding.');
                    }
                } finally {
                    setAwaitingPublish(false);
                }
            }

            if (!hasPersistentMarkerBeforeStart) {
                markBiddingShiftAsUnassigned(shift.id, originalResourceId);
                markerWrittenByStartFlow = true;
                const markerAfterWrite = resolvePersistedBiddingMarker(shift.id, 'start-flow-after-marker');
                logBiddingDebug('handleStartShiftBidding:marker-written', {
                    eventId: shift.id,
                    markerResolved: markerAfterWrite !== null,
                    startedAt: markerAfterWrite?.startedAt
                });
            }
            logBiddingInfo('handleStartShiftBidding:attempt-update-job-bidding', {
                eventId: shift.id,
                isUnassigned: shiftIsUnassigned,
                originalResourceId
            });
            try {
                await updateEvent({ isJobBidding: true });
                const persistedMarker = resolvePersistedBiddingMarker(shift.id, 'start-flow-marker-post-patch');
                logBiddingInfo('handleStartShiftBidding:open-for-bidding-patched', {
                    eventId: shift.id,
                    persistedMarker: !!persistedMarker
                });
                setEvent((prev) => prev ? ({
                    ...prev,
                    isJobBidding: true,
                    resourceId: '0',
                    originalResourceID: normalizePersistedBiddingResourceId(prev.originalResourceID) || originalResourceId
                }) : prev);
                updateShiftInfo(shift.id, {
                    isJobBidding: true,
                    resourceId: '0',
                    originalResourceID: originalResourceId
                });
                delete completedOptimisationByShiftRef.current[shift.id];
                removePersistedBiddingOptimisationMarker(shift.id, 'shift-bidding-started');
                removeInviteChecklistDraft(shift.id, 'shift-bidding-started');
                setInviteSelectionByResourceId({});
                setManualInviteResourceIds([]);
                logBiddingInfo('handleStartShiftBidding:open-for-bidding-enabled', {
                    eventId: shift.id,
                    originalResourceId,
                    persistedMarker: !!persistedMarker
                });
            } catch (error) {
                toast('error', 'Unable to start shift bidding. Please try again.');
                logBiddingDebug('handleStartShiftBidding:open-for-bidding-failed', {
                    eventId: shift.id,
                    error: (error as any)?.message ?? String(error)
                });
                if (markerWrittenByStartFlow) {
                    removePersistedBiddingUnassignedShiftMarker(shift.id);
                    markerWrittenByStartFlow = false;
                    logBiddingInfo('handleStartShiftBidding:marker-removed-on-failure', {
                        eventId: shift.id,
                        reason: 'open-for-bidding-failed'
                    });
                }
                return;
            }

            const cachedAllocations = shift.id ? (aiSuggestionsByShiftRef.current[shift.id] ?? []) : [];
            logBiddingInfo('handleStartShiftBidding:staff-tab-opened', {
                eventId: shift.id,
                unassignedDuringStart,
                cachedAllocationCount: cachedAllocations.length
            });
            setActiveTab('staff');
            if (!unassignedDuringStart && cachedAllocations.length === 0) {
                runAiSuggestions({ force: true, requestKeySuffix: 'unassigned', source: 'manual' });
            }
            const marker = resolvePersistedBiddingMarker(shift.id, 'start-flow-success');
            if (marker) {
                logBiddingDebug('handleStartShiftBidding:marker-verified', {
                    eventId: shift.id,
                    source: 'start-flow-end',
                    startedAt: marker.startedAt
                });
            }
        } finally {
            setStartingShiftBidding(false);
        }
    };
    const handleAssignResource = async (
        resource: IResource,
        e?: React.MouseEvent<any>,
        requireReason?: boolean
    ) => {
        if (e && typeof (e as any).stopPropagation === 'function') {
            (e as any).stopPropagation();
        }
        if (!event) return;
        if (requireReason) {
            window.prompt(
                'Please provide a brief reason for selecting this staff member over the top 3 suggestions:',
                ''
            );
        }
        setAssigningResourceId(resource.id);
        try {
            const success = await moveShift(event.id, resource.id);
            if (success) {
                removeInviteChecklistDraft(event.id, 'assigned-manually');
                removePersistedBiddingUnassignedShiftMarker(event.id);
                setInviteSelectionByResourceId({});
                setManualInviteResourceIds([]);
                setEvent((prev) => prev ? ({
                    ...prev,
                    resourceId: resource.id,
                    originalResourceID: prev.originalResourceID || resource.id
                }) : prev);
                setSelectedResource(resource);
            }
        } finally {
            setAssigningResourceId(null);
        }
    };
    const renderStaffMatch = (
        resource: IResource,
        allocation?: IAiAllocation,
        options?: {
            featured?: boolean;
            showAssign?: boolean;
            keyOverride?: string;
            requireReason?: boolean;
            actionType?: 'assign' | 'invite';
        }
    ) => {
        const matchPercent = allocation?.matchPercent ?? computeMatchPercent(resource);
        const resourceSkills = resource.characteristics.map((char) => char.name);
        const visibleSkills = resourceSkills.slice(0, 5);
        const extraSkills = resourceSkills.length - visibleSkills.length;
        const staffShiftCount = getResourceShifts(resource.id)?.length ?? 0;
        const resourceData = getResourceData(resource.id);
        const fallbackHoursWorked = (getResourceShifts(resource.id) ?? []).reduce((total, shift) => {
            const minutes = typeof shift.shiftLength === 'number' ? shift.shiftLength : 0;
            return total + minutes;
        }, 0) / 60;
        const hoursWorked = resourceData?.allSitesHoursWorked ?? resourceData?.hoursWorked ?? fallbackHoursWorked;
        const maxHours = resourceData?.maxHoursAllowedPerPayroll ?? resource.maximumWorkHourPerPayroll ?? null;
        const hoursLabel = hoursWorked != null && maxHours != null
            ? `${hoursWorked.toFixed(1)} / ${maxHours}`
            : placeholder;
        const distanceState = getStaffDistanceState(resource);
        const staffInitials = getInitials(resource.title || placeholder);
        const isExpanded = expandedStaffId === resource.id;
        const isFeatured = options?.featured;
        const showAssign = options?.showAssign ?? true;
        const requireReason = options?.requireReason ?? false;
        const assignButtonLabel = isUnassigned ? 'Assign' : 'Reassign';
        const isActionBusy = assigningResourceId === resource.id;
        const disableAction = !!assigningResourceId;
        const costLabel = formatAllocationMoney(allocation?.totalCost);
        const penaltyCostLabel = formatAllocationMoney(allocation?.penaltyCost);
        const penaltyCount = allocation?.penaltyCount ?? allocation?.penalties?.length ?? 0;
        const penaltyTooltip = getPenaltyTooltip(allocation);
        const hasCostValue = allocation?.totalCost != null;
        const hasPenaltyCostValue = allocation?.penaltyCost != null;

        return (
            <div
                key={options?.keyOverride ?? resource.id}
                className={`staff-list-item${isFeatured ? ' staff-list-item--featured' : ''}${isExpanded ? ' staff-list-item--expanded' : ''}`}
                onClick={() => setExpandedStaffId((prev) => (prev === resource.id ? null : resource.id))}
                role="button"
            >
                <div className={'staff-list-main'}>
                    <div className={'staff-avatar'}>
                        {resource.profileImageUrl ? (
                            <img src={resource.profileImageUrl} alt={resource.title} />
                        ) : (
                            <span>{staffInitials}</span>
                        )}
                    </div>
                    <div className={'staff-card-meta'}>
                        <div className={'staff-card-name'}>{resource.title || placeholder}</div>
                        <div className={'staff-card-role'}>Support Worker</div>
                    </div>
                    <div className={'staff-list-stats'}>
                        <div className={'staff-list-stat staff-list-stat--shifts'}>
                            <Clock24Regular />
                            <span>{staffShiftCount}</span>
                            <small>Shifts</small>
                        </div>
                        <div className={'staff-list-stat staff-list-stat--hours'}>
                            <Clock24Regular />
                            <span>{hoursLabel}</span>
                            <small>Hours / Max</small>
                        </div>
                        <div className={'staff-list-stat staff-list-stat--distance'}>
                            <Location24Regular />
                            <span title={distanceState?.tooltip ?? undefined}>
                                {distanceState?.distanceLabel ?? placeholder}
                            </span>
                            <small>Distance</small>
                        </div>
                    </div>
                    <div className={'staff-list-item-actions'}>
                        {allocation && (
                            <>
                                {hasCostValue && (
                                    <div
                                        className={'staff-metric-badge staff-metric-badge--cost'}
                                        title={`Estimated total cost ${costLabel}`}
                                    >
                                        <span className={'staff-metric-badge-label'}>Cost</span>
                                        <span className={'staff-metric-badge-value'}>{costLabel}</span>
                                    </div>
                                )}
                                {hasPenaltyCostValue && (
                                    <div
                                        className={`staff-metric-badge ${penaltyCount > 0 ? 'staff-metric-badge--penalty' : 'staff-metric-badge--neutral'}`}
                                        title={penaltyTooltip}
                                    >
                                        <span className={'staff-metric-badge-label'}>Penalties</span>
                                        <span className={'staff-metric-badge-value'}>{penaltyCostLabel}</span>
                                    </div>
                                )}
                            </>
                        )}
                        <div className={'staff-match-badge'}>
                            {matchPercent !== null ? `Matching Score ${matchPercent}` : placeholder}
                        </div>
                        {showAssign && (
                            <div className={'staff-list-assign'}>
                                <PrimaryButton
                                    className={'event-modal-rounded-button'}
                                    onClick={(e) => handleAssignResource(resource, e, requireReason)}
                                    disabled={disableAction}
                                >
                                    {isActionBusy ? <Spinner size={SpinnerSize.small} /> : assignButtonLabel}
                                </PrimaryButton>
                            </div>
                        )}
                    </div>
                </div>
                {isExpanded && (
                    <div className={'staff-list-details'}>
                        {allocation?.reason && (
                            <div className={'staff-list-reason'}>
                                {allocation.reason}
                            </div>
                        )}
                        <div className={'staff-card-note'}>
                            {resource.personalNote || placeholder}
                        </div>
                        <div className={'pill-group'}>
                            {(visibleSkills.length > 0 ? visibleSkills : [placeholder]).map((skill, index) => (
                                <span className={'event-pill event-pill--outline'} key={`${resource.id}-skill-${index}`}>
                                    {skill}
                                </span>
                            ))}
                            {extraSkills > 0 && (
                                <span className={'event-pill event-pill--outline'}>{`+${extraSkills}`}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };
    const renderInviteChecklistItem = (
        resource: IResource,
        allocation?: IAiAllocation,
        options?: {
            featured?: boolean;
            keyOverride?: string;
        }
    ) => {
        const matchPercent = allocation?.matchPercent ?? computeMatchPercent(resource);
        const resourceSkills = resource.characteristics.map((char) => char.name);
        const visibleSkills = resourceSkills.slice(0, 5);
        const extraSkills = resourceSkills.length - visibleSkills.length;
        const staffShiftCount = getResourceShifts(resource.id)?.length ?? 0;
        const resourceData = getResourceData(resource.id);
        const fallbackHoursWorked = (getResourceShifts(resource.id) ?? []).reduce((total, shift) => {
            const minutes = typeof shift.shiftLength === 'number' ? shift.shiftLength : 0;
            return total + minutes;
        }, 0) / 60;
        const hoursWorked = resourceData?.allSitesHoursWorked ?? resourceData?.hoursWorked ?? fallbackHoursWorked;
        const maxHours = resourceData?.maxHoursAllowedPerPayroll ?? resource.maximumWorkHourPerPayroll ?? null;
        const hoursLabel = hoursWorked != null && maxHours != null
            ? `${hoursWorked.toFixed(1)} / ${maxHours}`
            : placeholder;
        const distanceState = getStaffDistanceState(resource);
        const staffInitials = getInitials(resource.title || placeholder);
        const isExpanded = expandedStaffId === resource.id;
        const isFeatured = options?.featured;
        const isSelected = !!inviteSelectionByResourceId[resource.id];
        const selectionDisabled = autoInviting || !!invitingResourceId;
        const checkboxId = `invite-select-${event?.id ?? 'shift'}-${resource.id}`;
        const costLabel = formatAllocationMoney(allocation?.totalCost);
        const penaltyCostLabel = formatAllocationMoney(allocation?.penaltyCost);
        const penaltyCount = allocation?.penaltyCount ?? allocation?.penalties?.length ?? 0;
        const penaltyTooltip = getPenaltyTooltip(allocation);
        const hasCostValue = allocation?.totalCost != null;
        const hasPenaltyCostValue = allocation?.penaltyCost != null;

        return (
            <div
                key={options?.keyOverride ?? resource.id}
                className={`staff-list-item${isFeatured ? ' staff-list-item--featured' : ''}${isExpanded ? ' staff-list-item--expanded' : ''}`}
                onClick={() => setExpandedStaffId((prev) => (prev === resource.id ? null : resource.id))}
                role="button"
            >
                <div className={'staff-list-main'}>
                    <div className={'staff-avatar'}>
                        {resource.profileImageUrl ? (
                            <img src={resource.profileImageUrl} alt={resource.title} />
                        ) : (
                            <span>{staffInitials}</span>
                        )}
                    </div>
                    <div className={'staff-card-meta'}>
                        <div className={'staff-card-name'}>{resource.title || placeholder}</div>
                        <div className={'staff-card-role'}>Support Worker</div>
                    </div>
                    <div className={'staff-list-stats'}>
                        <div className={'staff-list-stat staff-list-stat--shifts'}>
                            <Clock24Regular />
                            <span>{staffShiftCount}</span>
                            <small>Shifts</small>
                        </div>
                        <div className={'staff-list-stat staff-list-stat--hours'}>
                            <Clock24Regular />
                            <span>{hoursLabel}</span>
                            <small>Hours / Max</small>
                        </div>
                        <div className={'staff-list-stat staff-list-stat--distance'}>
                            <Location24Regular />
                            <span title={distanceState?.tooltip ?? undefined}>
                                {distanceState?.distanceLabel ?? placeholder}
                            </span>
                            <small>Distance</small>
                        </div>
                    </div>
                    <div className={'staff-list-item-actions'}>
                        {allocation && (
                            <>
                                {hasCostValue && (
                                    <div
                                        className={'staff-metric-badge staff-metric-badge--cost'}
                                        title={`Estimated total cost ${costLabel}`}
                                    >
                                        <span className={'staff-metric-badge-label'}>Cost</span>
                                        <span className={'staff-metric-badge-value'}>{costLabel}</span>
                                    </div>
                                )}
                                {hasPenaltyCostValue && (
                                    <div
                                        className={`staff-metric-badge ${penaltyCount > 0 ? 'staff-metric-badge--penalty' : 'staff-metric-badge--neutral'}`}
                                        title={penaltyTooltip}
                                    >
                                        <span className={'staff-metric-badge-label'}>Penalties</span>
                                        <span className={'staff-metric-badge-value'}>{penaltyCostLabel}</span>
                                    </div>
                                )}
                            </>
                        )}
                        <div className={'staff-match-badge'}>
                            {matchPercent !== null ? `Matching Score ${matchPercent}` : 'Manual Add'}
                        </div>
                        {allocation?.reason ? (
                            <span className={'staff-invite-source-badge'}>Suggested Invitee</span>
                        ) : (
                            <span className={'staff-invite-source-badge staff-invite-source-badge--manual'}>Manual</span>
                        )}
                        <div
                            className={'staff-invite-checkbox'}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                className={'staff-invite-checkbox-input'}
                                id={checkboxId}
                                type={'checkbox'}
                                checked={isSelected}
                                disabled={selectionDisabled}
                                aria-label={isSelected ? 'Selected invitee' : 'Select invitee'}
                                onChange={(e) => handleToggleInviteSelection(resource.id, e.currentTarget.checked)}
                            />
                        </div>
                    </div>
                </div>
                {isExpanded && (
                    <div className={'staff-list-details'}>
                        {allocation?.reason && (
                            <div className={'staff-list-reason'}>
                                {allocation.reason}
                            </div>
                        )}
                        <div className={'staff-card-note'}>
                            {resource.personalNote || placeholder}
                        </div>
                        <div className={'pill-group'}>
                            {(visibleSkills.length > 0 ? visibleSkills : [placeholder]).map((skill, index) => (
                                <span className={'event-pill event-pill--outline'} key={`${resource.id}-invite-skill-${index}`}>
                                    {skill}
                                </span>
                            ))}
                            {extraSkills > 0 && (
                                <span className={'event-pill event-pill--outline'}>{`+${extraSkills}`}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };
    const renderMatchingUi = (options?: { actionType?: 'assign' | 'invite' }) => {
        const actionType = options?.actionType ?? 'assign';
        const isInviteMode = actionType === 'invite';
        const aiDisclaimer = isInviteMode
            ? 'Optimisation-generated suggestions may be inaccurate. Please verify before inviting.'
            : 'Optimisation-generated suggestions may be inaccurate. Please verify before assigning.';
        const requestKeySuffix = isUnassigned ? 'unassigned' : 'assigned';
        const inviteSelectionCount = selectedInviteResources.length;
        const featuredInviteEntries = inviteChecklistEntries.filter((entry) =>
            topMatches.some((match) => match.resource.id === entry.resource.id)
        );
        const nonFeaturedInviteEntries = inviteChecklistEntries.filter((entry) =>
            !featuredInviteEntries.some((featuredEntry) => featuredEntry.resource.id === entry.resource.id)
        );

        return (
            <>
                <section className={'event-modal-staff-section'}>
                    <div className={'staff-list-header'}>
                        <Text variant={'large'} block className={'staff-section-title'}>
                            Top Matches
                        </Text>
                        <div className={'staff-list-actions'}>
                            <DefaultButton
                                className={'ai-rules-button event-modal-rounded-button'}
                                onClick={openRsoConfiguration}
                                disabled={aiLoading}
                                title={`Current RSO source: ${activePresetLabel}`}
                            >
                                Configure RSO
                            </DefaultButton>
                            <DefaultButton
                                className={'ai-rules-button event-modal-rounded-button'}
                                onClick={() => runAiSuggestions({ force: true, requestKeySuffix, source: 'manual' })}
                                disabled={aiLoading}
                            >
                                {aiLoading ? 'Running...' : findBestMatchButtonLabel}
                            </DefaultButton>
                            <DefaultButton
                                className={startShiftBiddingButtonClassName}
                                onClick={() => {
                                    void handleStartShiftBidding();
                                }}
                                disabled={disableStartShiftBidding}
                            >
                                {startShiftBiddingButtonLabel}
                            </DefaultButton>
                            {(event?.isJobBidding || hasPersistedBiddingUnassignedMarker) && (
                                <DefaultButton
                                    className={'event-modal-rounded-button'}
                                    onClick={() => {
                                        void handleRefreshBiddingStatus();
                                    }}
                                    disabled={refreshingBiddingStatus}
                                >
                                    {refreshingBiddingStatus ? <Spinner size={SpinnerSize.small} /> : 'Refresh'}
                                </DefaultButton>
                            )}
                            <span ref={aiRulesTargetRef}>
                                <DefaultButton
                                    className={'ai-rules-help-button'}
                                    onClick={() => setShowAiRules((prev) => !prev)}
                                    ariaLabel={'AI rules'}
                                    title={'AI rules'}
                                >
                                    ?
                                </DefaultButton>
                            </span>
                        </div>
                    </div>
                    {showAiRules && aiRulesTargetRef.current && (
                        <Callout
                            className={'ai-rules-callout-root'}
                            target={aiRulesTargetRef.current}
                            onDismiss={() => setShowAiRules(false)}
                            setInitialFocus
                        >
                            <div className={'ai-rules-callout'}>
                                <div className={'ai-rules-title'}>How suggestions are generated</div>
                                <ul className={'ai-rules-list'}>
                                    {aiRules.map((rule, index) => (
                                        <li key={`ai-rule-${index}`}>{rule}</li>
                                    ))}
                                </ul>
                            </div>
                        </Callout>
                    )}
                    {(aiLoading || aiStatusMessage) && (
                        <div className={aiStatusClassName}>
                            {aiLoading && <Spinner size={SpinnerSize.small} />}
                            <div className={'staff-ai-status-text'}>
                                {aiStatusMessage ?? (aiLoading ? 'Working...' : '')}
                            </div>
                        </div>
                    )}
                    <div className={'staff-ai-disclaimer'}>
                        {aiDisclaimer}
                    </div>
                    {!hasCompletedOptimisationForShift && !hasShiftBiddingStarted && (
                        <div className={'staff-ai-disclaimer staff-ai-disclaimer--action-gate'}>
                            Run Optimisations Suggestion to enable Start Shift Bidding for this shift.
                        </div>
                    )}
                    {isInviteMode && (
                        <>
                            <div className={'staff-ai-disclaimer'}>
                                {!event?.isJobBidding
                                    ? 'This shift is not marked Open for Bidding.'
                                    : !isUnassigned
                                        ? 'This shift is already assigned. Invitations are disabled.'
                                        : 'Select the resources to invite, then click Send Invite. Starting shift bidding does not send invitations automatically.'
                                }
                            </div>
                            <div className={'staff-invite-toolbar'}>
                                <DefaultButton
                                    className={'event-modal-rounded-button'}
                                    onClick={handleSelectAllInvites}
                                    disabled={autoInviting || inviteChecklistResourceIds.length < 1}
                                >
                                    Select All
                                </DefaultButton>
                                <DefaultButton
                                    className={'event-modal-rounded-button'}
                                    onClick={handleUnselectAllInvites}
                                    disabled={autoInviting || inviteChecklistResourceIds.length < 1}
                                >
                                    Unselect All
                                </DefaultButton>
                                <PrimaryButton
                                    className={'event-modal-rounded-button'}
                                    onClick={() => {
                                        void handleSendSelectedInvites();
                                    }}
                                    disabled={autoInviting || invitingResourceId !== null || inviteSelectionCount < 1}
                                >
                                    {autoInviting ? <Spinner size={SpinnerSize.small} /> : `Send Invite${inviteSelectionCount === 1 ? '' : inviteSelectionCount > 1 ? ` (${inviteSelectionCount})` : ''}`}
                                </PrimaryButton>
                            </div>
                            <div className={'staff-invite-manual-add'}>
                                <ComboBox
                                    componentRef={manualInviteComboBoxRef}
                                    label={'Add Resource Manually'}
                                    placeholder={'Click to browse or type to search resources'}
                                    options={manualInviteOptions}
                                    selectedKey={manualInviteResourceIds}
                                    text={manualInviteSearchText}
                                    multiSelect
                                    useComboBoxAsMenuWidth
                                    dropdownMaxWidth={560}
                                    allowFreeform
                                    autoComplete={'off'}
                                    onInputValueChange={setManualInviteSearchText}
                                    onChange={handleChangeManualInviteResources}
                                />
                                <div className={'staff-invite-manual-add-note'}>
                                    {!manualInviteSearchQuery
                                        ? manualInviteOptions.length > 0
                                            ? `Showing ${manualInviteOptions.length} resources. Type to narrow the list or select one or more resources directly.`
                                            : 'No available resources found.'
                                        : manualInviteOptions.length > 0
                                            ? `Showing ${manualInviteOptions.length} match${manualInviteOptions.length === 1 ? '' : 'es'}. Select one or more resources to add them to the invite list.`
                                            : 'No matching resources found.'
                                    }
                                </div>
                            </div>
                        </>
                    )}
                    {currentInvitationStatuses.length > 0 && (
                        <section className={'event-modal-staff-section'}>
                            <Text variant={'large'} block className={'staff-section-title'}>
                                {isInviteMode ? 'Current Invitations' : 'Invitation History'}
                            </Text>
                            <div className={'staff-invitation-status-list'}>
                                {currentInvitationStatuses.map((invitee) => (
                                    <div className={'staff-invitation-status-row'} key={`invite-status-${invitee.resourceId}`}>
                                        <span className={'staff-invitation-status-name'}>{invitee.resourceName}</span>
                                        <div className={'staff-invitation-status-actions'}>
                                            <span className={`staff-invitation-status-pill staff-invitation-status-pill--${invitee.status.replace(/\s+/g, '-')}`}>
                                                {invitee.statusLabel}
                                            </span>
                                            {invitee.assignmentLabel && (
                                                <span className={'staff-invitation-status-pill staff-invitation-status-pill--assigned'}>
                                                    {invitee.assignmentLabel}
                                                </span>
                                            )}
                                            {isInviteMode && invitee.status === 'expressed interest' && invitee.resource && (
                                                <PrimaryButton
                                                    className={'event-modal-rounded-button'}
                                                    onClick={(e) => void handleAssignResource(invitee.resource as IResource, e)}
                                                    disabled={
                                                        !isUnassigned
                                                        || awaitingPublish
                                                        || awaitingSave
                                                        || autoInviting
                                                        || !!invitingResourceId
                                                        || (!!assigningResourceId && assigningResourceId !== invitee.resourceId)
                                                    }
                                                >
                                                    {assigningResourceId === invitee.resourceId
                                                        ? <Spinner size={SpinnerSize.small} />
                                                        : 'Choose'}
                                                </PrimaryButton>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                    <div className={'staff-list staff-list--featured'}>
                        {isInviteMode
                            ? featuredInviteEntries.map(({ resource, allocation }, index) =>
                                renderInviteChecklistItem(resource, allocation, {
                                    featured: true,
                                    keyOverride: `${allocation?.jobId ?? event?.id ?? 'shift'}-${resource.id}-${index}`
                                })
                            )
                            : topMatches.length > 0 &&
                                topMatches.map(({ allocation, resource }, index) =>
                                    renderStaffMatch(resource, allocation, {
                                        featured: true,
                                        keyOverride: `${allocation.jobId}-${allocation.resourceId}-${index}`,
                                        requireReason: false,
                                        actionType
                                    })
                                )}
                    </div>
                    {isInviteMode && inviteChecklistEntries.length < 1 && !aiLoading && (
                        <div className={'info-value'}>
                            {currentInvitationStatuses.length > 0
                                ? 'Invitations are already active for this shift. Add more resources manually if required.'
                                : 'Run Find Best Match or add a resource manually to build the invite list.'}
                        </div>
                    )}
                </section>
                {((isInviteMode && nonFeaturedInviteEntries.length > 0) || (!isInviteMode && otherMatches.length > 0)) && (
                    <section className={'event-modal-staff-section'}>
                        <div className={'staff-list-header'}>
                            <Text variant={'large'} block className={'staff-section-title'}>
                                {isInviteMode ? 'Invite List' : 'Other Matches'}
                            </Text>
                            {!isInviteMode && (
                                <DefaultButton
                                    className={'event-modal-rounded-button staff-show-more-button'}
                                    onClick={() => setShowAllMatches((prev) => !prev)}
                                >
                                    {showAllMatches ? 'Show less' : 'Show more'}
                                </DefaultButton>
                            )}
                        </div>
                        <div className={'staff-list'}>
                            {isInviteMode
                                ? nonFeaturedInviteEntries.map(({ resource, allocation }, index) =>
                                    renderInviteChecklistItem(resource, allocation, {
                                        keyOverride: `${allocation?.jobId ?? event?.id ?? 'shift'}-${resource.id}-${index}`
                                    })
                                )
                                : (showAllMatches ? otherMatches : []).map(({ allocation, resource }, index) =>
                                    renderStaffMatch(resource, allocation, {
                                        keyOverride: `${allocation.jobId}-${allocation.resourceId}-${index}`,
                                        requireReason: true,
                                        actionType
                                    })
                                )}
                        </div>
                    </section>
                )}
            </>
        );
    };

    return (
        <>
            <Modal
                isOpen={open}
                onDismiss={() => setOpen(false)}
                isModeless={false}
                containerClassName={'eventModal eventModal--details'}
            >
                <AppInsightsErrorBoundary
                    onError={() => renderGenericFallbackUi({
                        mainHeading: 'Something went wrong whilst trying to load the shift',
                        subHeading: 'Please try again in a few minutes'
                    })}
                    appInsights={reactPlugin}
                >
                    <div className={'event-modal-shell'}>
                        <div className={'event-modal-header'}>
                            <div className={'event-modal-heading'}>
                                <Text variant={'xLarge'} block>
                                    Shift Details
                                </Text>
                                <Text variant={'small'} block className={'event-modal-subtitle'}>
                                    <span>Shift ID:</span>
                                    {event ? (
                                        <Link
                                            target={"_blank"}
                                            href={buildCrmUrl(getEnvironmentInfo(), 'msdyn_workorder', event.id)}
                                        >
                                            {shiftIdLabel}
                                        </Link>
                                    ) : (
                                        <span>{placeholder}</span>
                                    )}
                                </Text>
                            </div>
                            <IconButton color={'#000000'} onClick={() => setOpen(false)}>
                                <Dismiss24Regular color={"#000000"} />
                            </IconButton>
                        </div>

                        {event && (
                            <div className={'event-modal-body'}>
                                <div className={'event-modal-meta'}>
                                    <div className={'event-modal-meta-card'}>
                                        <div className={'event-modal-meta-icon'}>
                                            <Person24Regular />
                                        </div>
                                        <div>
                                            <div className={'event-modal-meta-label'}>Client</div>
                                            <div className={'event-modal-meta-value'}>
                                                {!isIndividualWorkOrder && event.serviceAccount?.accountId ? (
                                                    <Link
                                                        target={"_blank"}
                                                        href={buildCrmUrl(getEnvironmentInfo(), "account", event.serviceAccount.accountId)}
                                                    >
                                                        {clientName}
                                                    </Link>
                                                ) : (
                                                    clientName
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={'event-modal-meta-card'}>
                                        <div className={'event-modal-meta-icon'}>
                                            <CalendarLtr24Regular />
                                        </div>
                                        <div>
                                            <div className={'event-modal-meta-label'}>Date</div>
                                            <div className={'event-modal-meta-value'}>{shiftDateLabel}</div>
                                        </div>
                                    </div>
                                    <div className={'event-modal-meta-card'}>
                                        <div className={'event-modal-meta-icon'}>
                                            <Clock24Regular />
                                        </div>
                                        <div>
                                            <div className={'event-modal-meta-label'}>Time</div>
                                            <div className={'event-modal-meta-value'}>{shiftTimeLabel}</div>
                                        </div>
                                    </div>
                                    <div className={'event-modal-meta-card'}>
                                        <div className={'event-modal-meta-icon'}>
                                            <Location24Regular />
                                        </div>
                                        <div>
                                            <div className={'event-modal-meta-label'}>Location</div>
                                            <div className={'event-modal-meta-value'}>{locationLabel}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className={'event-modal-summary'}>
                                    <div className={'summary-label'}>Summary</div>
                                    <div className={'summary-value'}>{shiftSummary}</div>
                                </div>

                                <div className={'event-modal-tabs'}>
                                    <div
                                        className={`event-modal-tab${activeTab === 'details' ? ' event-modal-tab--active' : ''}`}
                                        onClick={() => setActiveTab('details')}
                                    >
                                        <DocumentText24Regular />
                                        Shift Details
                                    </div>
                                    <div
                                        className={`event-modal-tab${activeTab === 'staff' ? ' event-modal-tab--active' : ''}`}
                                        onClick={() => setActiveTab('staff')}
                                    >
                                        <People24Regular />
                                        Staff/ Resource
                                    </div>
                                    <div
                                        className={`event-modal-tab${activeTab === 'alerts' ? ' event-modal-tab--active' : ''}`}
                                        onClick={() => setActiveTab('alerts')}
                                    >
                                        <Alert24Regular />
                                        Alerts
                                        {alertsCount > 0 && (
                                            <span className={'event-modal-tab-badge'}>{alertsCount}</span>
                                        )}
                                    </div>
                                    <div
                                        className={`event-modal-tab${activeTab === 'notes' ? ' event-modal-tab--active' : ''}`}
                                        onClick={() => setActiveTab('notes')}
                                    >
                                        <Note24Regular />
                                        Notes & History
                                    </div>
                                </div>

                                <div className={'event-modal-scroll'} ref={scrollContainerRef}>
                                    {activeTab === 'details' && (
                                        <>
                                    <section className={'event-modal-section'}>
                                        <div className={'event-modal-section-header'}>
                                            <div className={'section-icon'} />
                                            <Text variant={'mediumPlus'} block>
                                                Service Information
                                            </Text>
                                        </div>
                                        <div className={'event-modal-info-grid'}>
                                            {detailInformationRows.map((row, index) => (
                                                <div
                                                    className={'info-item'}
                                                    key={`detail-info-${row.label}-${index}`}
                                                    style={{ gridColumn: `span ${Math.min(3, Math.max(1, row.span ?? 1))}` }}
                                                >
                                                    <div className={'info-label'}>{row.label}</div>
                                                    <div className={'info-value'}>{row.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                    <section className={'event-modal-section'}>
                                        <div className={'event-modal-section-header'}>
                                            <div className={'section-icon section-icon--skills'} />
                                            <Text variant={'mediumPlus'} block>
                                                Skills and Certifications Required
                                            </Text>
                                        </div>
                                        {activeSkills.length > 0 ? (
                                            <div className={'pill-group'}>
                                                {activeSkills.map((skill, index) => (
                                                    <span className={'event-pill'} key={`${skill}-${index}`}>{skill}</span>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className={'info-value'}>No skills required for this shift.</div>
                                        )}
                                    </section>
                                    {showServiceRequirements && (
                                        <section className={'event-modal-section'}>
                                            <div className={'event-modal-section-header'}>
                                                <div className={'section-icon section-icon--requirements'} />
                                                <Text variant={'mediumPlus'} block>
                                                    Service Requirements
                                                </Text>
                                            </div>
                                            <div className={'pill-group'}>
                                                {['****', '****', '****', '****'].map((item, index) => (
                                                    <span className={'event-pill event-pill--neutral'} key={`req-${index}`}>{item}</span>
                                                ))}
                                            </div>
                                            <div className={'event-modal-subsection'}>
                                                <div className={'info-label'}>Dietary Requirements</div>
                                                <div className={'pill-group'}>
                                                    {['****', '****'].map((item, index) => (
                                                        <span className={'event-pill event-pill--warning'} key={`diet-${index}`}>{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </section>
                                    )}
                                    {showCostBreakdown && (
                                        <section className={'event-modal-section'}>
                                            <div className={'event-modal-section-header'}>
                                                <div className={'section-icon section-icon--cost'} />
                                                <Text variant={'mediumPlus'} block>
                                                    Cost Breakdown
                                                </Text>
                                            </div>
                                            <div className={'event-modal-cost-header'}>
                                                <div className={'info-value'}>{assignedStaffLabel === 'Unassigned' ? placeholder : assignedStaffLabel}</div>
                                                <div className={'event-modal-cost-total'}>
                                                    {costTotal && costTotal > 0 ? `$${costTotal.toFixed(2)}` : placeholder}
                                                </div>
                                            </div>
                                            <div className={'event-modal-cost-list'}>
                                                {[
                                                    { label: 'Base pay', value: placeholder },
                                                    { label: 'Penalty (50%)', value: placeholder },
                                                    { label: 'Overtime', value: placeholder }
                                                ].map((item, index) => (
                                                    <div className={'event-modal-cost-row'} key={`cost-${index}`}>
                                                        <span>{item.label}</span>
                                                        <span>{item.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                    {isEditOpen && (
                                        <section className={'event-modal-section event-modal-section--edit'} ref={editSectionRef}>
                                            <div className={'event-modal-section-header'}>
                                                <div className={'section-icon section-icon--edit'} />
                                                <Text variant={'mediumPlus'} block>
                                                    Edit Shift
                                                </Text>
                                            </div>
                                            <details className="details-box" open={openedDetails === 1}>
                                                <summary onClick={(event) => handleToggleDetails(event, 1)}>Modify Date and
                                                    Time
                                                </summary>
                                                <DatePicker
                                                    label="Date:"
                                                    value={event.start}
                                                    onSelectDate={handleChangeDate}
                                                />
                                                <TimePicker
                                                    label="Start Time:"
                                                    useHour12
                                                    increments={15}
                                                    defaultValue={event.start}
                                                    onChange={handleChangeStart}
                                                    calloutProps={{ calloutMinWidth: 120, calloutMaxHeight: 200 }}
                                                />
                                                <ComboBox
                                                    label="Duration:"
                                                    onChange={handleChangeDuration}
                                                    selectedKey={event.shiftLength}
                                                    calloutProps={{
                                                        calloutMinWidth: 120,
                                                        calloutMaxHeight: 200,
                                                        directionalHint: DirectionalHint.bottomRightEdge
                                                    }}
                                                    options={[
                                                        { key: 1, text: '1 minute' },
                                                        { key: 15, text: '15 minutes' },
                                                        { key: 30, text: '30 minutes' },
                                                        { key: 45, text: '45 minutes' },
                                                        { key: 60, text: '1 hour' },
                                                        { key: 90, text: '1.5 hours' },
                                                        { key: 120, text: '2 hours' },
                                                        { key: 150, text: '2.5 hours' },
                                                        { key: 180, text: '3 hours' },
                                                        { key: 210, text: '3.5 hours' },
                                                        { key: 240, text: '4 hours' },
                                                        { key: 270, text: '4.5 hours' },
                                                        { key: 300, text: '5 hours' },
                                                        { key: 330, text: '5.5 hours' },
                                                        { key: 360, text: '6 hours' },
                                                        { key: 390, text: '6.5 hours' },
                                                        { key: 420, text: '7 hours' },
                                                        { key: 450, text: '7.5 hours' },
                                                        { key: 480, text: '8 hours' },
                                                        { key: 1440, text: '1 day' },
                                                        { key: 2880, text: '2 days' },
                                                        { key: 4320, text: '3 days' },
                                                    ]}
                                                />
                                            </details>

                                            <details className="details-box" open={openedDetails === 2}>
                                                <summary onClick={(event) => handleToggleDetails(event, 2)}>Modify Shift
                                                    Details
                                                </summary>
                                                <TextField
                                                    label="Title:"
                                                    value={title}
                                                    onChange={handleChangeTitle}
                                                    onBlur={handleUpdateTitle}
                                                    onKeyDown={handleTitleInputEnter}
                                                />
                                                <DefaultButton
                                                    className={'shift-cancel-button'}
                                                    disabled={event.bookableResourceStatus === EBookingStatus.Cancelled || event.resourceId=='' || event.resourceId=='0'|| awaitingSave || awaitingPublish}
                                                    onClick={() => lateClientCancel(event)}
                                                >
                                                    {event.bookableResourceStatus === EBookingStatus.Cancelled ? 'Cancelled' : 'Late Client Cancel'}
                                                </DefaultButton>

                                                <DefaultButton
                                                    className={'shift-cancel-button'}
                                                    disabled={event.bookableResourceStatus === EBookingStatus.Cancelled || event.resourceId=='' || event.resourceId=='0' || awaitingSave || awaitingPublish}
                                                    onClick={() => cancelShift(event)}
                                                >
                                                    {event.bookableResourceStatus === EBookingStatus.Cancelled ? 'Cancelled' : 'Cancel Shift'}
                                                </DefaultButton>
                                            </details>
                                            {isCancellationReviewPending(event) &&
                                                <details className="details-box" open={openedDetails === 3}>
                                                    <summary onClick={(event) => handleToggleDetails(event, 3)}>Cancellation Request Details </summary>
                                                    <TextField
                                                        label="Support Worker Comments"
                                                        multiline
                                                        rows={2}
                                                        maxLength={500}
                                                        resizable={false}
                                                        placeholder={event.cancellationRequestComments}
                                                        readOnly={true}
                                                    />
                                                </details>

                                            }

                                            {isCancellationReviewPending(event) &&
                                                <div className={'cancel-request-container'} style={{ boxShadow: theme.effects.elevation16 }}>
                                                    <div><strong>Cancellation Requested</strong></div>
                                                    <TextField multiline rows={2} maxLength={250} resizable={false} disabled={!cancellationRequestInFuture}
                                                        placeholder="Provide an optional reason for approve/decline"
                                                        value={cancellationRequestComments} onChange={handleCancellationRequestCommentsChange} />
                                                    <div className={'button-cancel-request-container'}>
                                                        <DefaultButton onClick={() => approveRejectShift(true)}
                                                            title={cancellationRequestInFuture ? 'Shift will be unassigned and you need to reassign to somebody else.' : 'Cancellation request is in past and cannot be approved/rejected.'}
                                                            disabled={!cancellationRequestInFuture}>
                                                            {awaitingSave || awaitingPublish ? <Spinner /> : 'Approve'}
                                                        </DefaultButton>
                                                        <DefaultButton onClick={() => approveRejectShift(false)}
                                                            title={cancellationRequestInFuture ? 'Shift will be kept assigned to the support worker.' : 'Cancellation request is in past and cannot be approved/rejected.'}
                                                            disabled={!cancellationRequestInFuture} >
                                                            {awaitingSave || awaitingPublish ? <Spinner /> : 'Decline'}
                                                        </DefaultButton>
                                                    </div>
                                                </div>
                                            }

                                            <div className={'event-modal-edit-actions'}>
                                                <PrimaryButton disabled={!(event?.detailsModified) || awaitingSave || awaitingPublish}
                                                    onClick={() => handleSaveShift()}>{awaitingSave ?
                                                        <Spinner /> : 'Save'}</PrimaryButton>
                                                <PrimaryButton disabled={event?.changeStatus === 0 || awaitingSave || awaitingPublish}
                                                    onClick={handlePublish}>{awaitingPublish ?
                                                        <Spinner /> : `Publish${event?.detailsModified && event?.changeStatus !== 0 ? '/Save' : ''}`}</PrimaryButton>
                                            </div>
                                        </section>
                                    )}
                                        </>
                                    )}
                                    {activeTab === 'staff' && (
                                        <>
                                            {event.isJobBidding && isUnassigned ? (
                                                <>{renderMatchingUi({ actionType: 'invite' })}</>
                                            ) : (
                                                <>
                                                    {isUnassigned ? (
                                                        <>{renderMatchingUi()}</>
                                                    ) : (
                                                        <>
                                                            <section className={'event-modal-staff-section'}>
                                                                <Text variant={'large'} block className={'staff-section-title'}>
                                                                    Assigned Staff
                                                                </Text>
                                                                <div className={'staff-card staff-card--assigned'}>
                                                                    <div className={'staff-card-left'}>
                                                                        <div className={'staff-avatar staff-avatar--lg'}>
                                                                            {assignedResource?.profileImageUrl ? (
                                                                                <img src={assignedResource.profileImageUrl} alt={assignedStaffName} />
                                                                            ) : (
                                                                                <span>{assignedInitials}</span>
                                                                            )}
                                                                        </div>
                                                                        <div className={'staff-card-meta'}>
                                                                            <div className={'staff-card-name'}>{assignedStaffName}</div>
                                                                            <div className={'staff-card-role'}>Support Worker</div>
                                                                            {assignedShiftBiddingInvitee && (
                                                                                <div
                                                                                    className={'staff-card-assignment-badge'}
                                                                                    title={`Assigned via shift bidding${assignedShiftBiddingInvitee.statusLabel ? ` (${assignedShiftBiddingInvitee.statusLabel})` : ''}`}
                                                                                >
                                                                                    <People24Regular />
                                                                                    <span>Assigned via shift bidding</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className={'staff-card-inline-details'}>
                                                                        <div className={'staff-card-inline-main'}>
                                                                            <div className={'staff-card-row staff-card-row--assigned'}>
                                                                                <Clock24Regular />
                                                                                <span>{shiftTimeLabel}</span>
                                                                            </div>
                                                                            <div className={'staff-card-row staff-card-row--assigned'}>
                                                                                <Phone24Regular />
                                                                                {assignedPhoneDial ? (
                                                                                    <a className={'staff-card-link'} href={`tel:${assignedPhoneDial}`}>
                                                                                        {assignedPhone}
                                                                                    </a>
                                                                                ) : (
                                                                                    <span>{assignedPhone}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className={'staff-card-row staff-card-row--assigned staff-card-row--assigned-email'}>
                                                                        <Mail24Regular />
                                                                        <span>{assignedEmail}</span>
                                                                    </div>
                                                                </div>
                                                            </section>
                                                            <section className={'event-modal-staff-section'}>
                                                                <div className={'staff-list-header'}>
                                                                    <Text variant={'large'} block className={'staff-section-title'}>
                                                                        Optimisations Suggestion
                                                                    </Text>
                                                                <div className={'staff-list-actions'}>
                                                                        <DefaultButton
                                                                            className={'ai-rules-button event-modal-rounded-button'}
                                                                            onClick={openRsoConfiguration}
                                                                            disabled={aiLoading}
                                                                            title={`Current RSO source: ${activePresetLabel}`}
                                                                        >
                                                                            Configure RSO
                                                                        </DefaultButton>
                                                                        <DefaultButton
                                                                            className={'ai-rules-button event-modal-rounded-button'}
                                                                            onClick={() => runAiSuggestions({ force: true, requestKeySuffix: 'assigned', source: 'manual' })}
                                                                            disabled={aiLoading}
                                                                        >
                                                                            {aiLoading ? 'Running...' : findBestMatchButtonLabel}
                                                                        </DefaultButton>
                                                                        <DefaultButton
                                                                            className={startShiftBiddingButtonClassName}
                                                                            onClick={() => {
                                                                                void handleStartShiftBidding();
                                                                            }}
                                                                            disabled={disableStartShiftBidding}
                                                                        >
                                                                            {startShiftBiddingButtonLabel}
                                                                        </DefaultButton>
                                                                        {(event?.isJobBidding || hasPersistedBiddingUnassignedMarker) && (
                                                                            <DefaultButton
                                                                                className={'event-modal-rounded-button'}
                                                                                onClick={() => {
                                                                                    void handleRefreshBiddingStatus();
                                                                                }}
                                                                                disabled={refreshingBiddingStatus}
                                                                            >
                                                                                {refreshingBiddingStatus ? <Spinner size={SpinnerSize.small} /> : 'Refresh'}
                                                                            </DefaultButton>
                                                                        )}
                                                                        <span ref={aiRulesTargetRef}>
                                                                            <DefaultButton
                                                                                className={'ai-rules-help-button'}
                                                                                onClick={() => setShowAiRules((prev) => !prev)}
                                                                                ariaLabel={'AI rules'}
                                                                                title={'AI rules'}
                                                                            >
                                                                                ?
                                                                            </DefaultButton>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                {showAiRules && aiRulesTargetRef.current && (
                                                                    <Callout
                                                                        className={'ai-rules-callout-root'}
                                                                        target={aiRulesTargetRef.current}
                                                                        onDismiss={() => setShowAiRules(false)}
                                                                        setInitialFocus
                                                                    >
                                                                        <div className={'ai-rules-callout'}>
                                                                            <div className={'ai-rules-title'}>How suggestions are generated</div>
                                                                            <ul className={'ai-rules-list'}>
                                                                                {aiRules.map((rule, index) => (
                                                                                    <li key={`ai-rule-${index}`}>{rule}</li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    </Callout>
                                                                )}
                                                                {(aiLoading || aiStatusMessage) && (
                                                                    <div className={aiStatusClassName}>
                                                                        {aiLoading && <Spinner size={SpinnerSize.small} />}
                                                                        <div className={'staff-ai-status-text'}>
                                                                            {aiStatusMessage ?? (aiLoading ? 'Working...' : '')}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className={'staff-ai-disclaimer'}>
                                                                    Optimisation-generated suggestions may be inaccurate. Please verify before assigning.
                                                                </div>
                                                                {!hasCompletedOptimisationForShift && !hasShiftBiddingStarted && (
                                                                    <div className={'staff-ai-disclaimer staff-ai-disclaimer--action-gate'}>
                                                                        Run Optimisations Suggestion to enable Start Shift Bidding for this shift.
                                                                    </div>
                                                                )}
                                                                {aiMatches.length > 0 ? (
                                                                    <div className={'staff-list staff-list--featured'}>
                                                                        {topMatches.map(({ allocation, resource }, index) =>
                                                                            renderStaffMatch(resource, allocation, {
                                                                                featured: true,
                                                                                keyOverride: `${allocation.jobId}-${allocation.resourceId}-${index}`,
                                                                                requireReason: false
                                                                            })
                                                                        )}
                                                                    </div>
                                                                ) : (!aiLoading && (
                                                                    <div className={'info-value'}>
                                                                        No suggestions yet. Find Best Match to find a replacement.
                                                                    </div>
                                                                ))}
                                                            </section>
                                                            {otherMatches.length > 0 && (
                                                                <section className={'event-modal-staff-section'}>
                                                                    <div className={'staff-list-header'}>
                                                                        <Text variant={'large'} block className={'staff-section-title'}>
                                                                            Other Matches
                                                                        </Text>
                                                                        <DefaultButton
                                                                            className={'event-modal-rounded-button staff-show-more-button'}
                                                                            onClick={() => setShowAllMatches((prev) => !prev)}
                                                                        >
                                                                            {showAllMatches ? 'Show less' : 'Show more'}
                                                                        </DefaultButton>
                                                                    </div>
                                                                    <div className={'staff-list'}>
                                                                        {(showAllMatches ? otherMatches : []).map(({ allocation, resource }, index) =>
                                                                            renderStaffMatch(resource, allocation, {
                                                                                keyOverride: `${allocation.jobId}-${allocation.resourceId}-${index}`,
                                                                                requireReason: true
                                                                            })
                                                                        )}
                                                                    </div>
                                                                </section>
                                                            )}
                                                            {showCachedMatches && (
                                                                <section className={'event-modal-staff-section'}>
                                                                    <Text variant={'large'} block className={'staff-section-title'}>
                                                                        Optimisations Suggestion (cached)
                                                                    </Text>
                                                                    <div className={'staff-list'}>
                                                                        {cachedMatches.map(({ allocation, resource }, index) =>
                                                                            renderStaffMatch(resource, allocation, {
                                                                                showAssign: false,
                                                                                keyOverride: `${allocation.jobId}-${allocation.resourceId}-${index}`
                                                                            })
                                                                        )}
                                                                    </div>
                                                                </section>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'alerts' && (
                                        <>
                                            {softAlertGroups.length > 0 && (
                                                <section className={'alerts-block alerts-block--soft'}>
                                                    <div className={'alerts-block-header'}>
                                                        <div className={'alerts-block-title'}>Soft Alerts</div>
                                                        <span className={'alerts-block-count'}>{softAlerts.length}</span>
                                                        <span className={'alerts-block-action alerts-block-action--soft'}>
                                                            Review Recommended
                                                        </span>
                                                    </div>
                                                    {softAlertGroups.map((group, groupIndex) => (
                                                        <div className={'alerts-card alerts-card--soft'} key={`soft-${group.title}-${groupIndex}`}>
                                                            <div className={'alerts-card-title'}>{group.title}</div>
                                                            <ul className={'alerts-card-list'}>
                                                                {group.items.map((alert, alertIndex) => (
                                                                    <li key={`${alert.referenceId || group.title}-soft-${alertIndex}`}>
                                                                        {renderAlertMessage(alert.message)}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </section>
                                            )}
                                            {hardAlertGroups.length > 0 && (
                                                <section className={'alerts-block alerts-block--hard'}>
                                                    <div className={'alerts-block-header'}>
                                                        <div className={'alerts-block-title'}>Hard Alerts</div>
                                                        <span className={'alerts-block-count'}>{hardAlerts.length}</span>
                                                        <span className={'alerts-block-action alerts-block-action--hard'}>
                                                            Attention Required
                                                        </span>
                                                    </div>
                                                    {hardAlertGroups.map((group, groupIndex) => (
                                                        <div className={'alerts-card alerts-card--hard'} key={`hard-${group.title}-${groupIndex}`}>
                                                            <div className={'alerts-card-title'}>{group.title}</div>
                                                            <ul className={'alerts-card-list'}>
                                                                {group.items.map((alert, alertIndex) => (
                                                                    <li key={`${alert.referenceId || group.title}-hard-${alertIndex}`}>
                                                                        {renderAlertMessage(alert.message)}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </section>
                                            )}
                                            {shiftAlerts.length === 0 && (
                                                <section className={'event-modal-section'}>
                                                    <div className={'info-value'}>{placeholder}</div>
                                                </section>
                                            )}
                                        </>
                                    )}
                                    {activeTab === 'notes' && (
                                        <section className={'event-modal-section history-section'}>
                                            <div className={'history-header'}>
                                                <div className={'history-header-icon'} />
                                                <Text variant={'mediumPlus'} block>
                                                    Shift History
                                                </Text>
                                            </div>
                                            {historyLoading && (
                                                <div className={'history-state'}>
                                                    <Spinner size={SpinnerSize.small} label={'Loading history...'} />
                                                </div>
                                            )}
                                            {!historyLoading && historyItems.length === 0 && (
                                                <div className={'history-empty'}>{historyError || placeholder}</div>
                                            )}
                                            {!historyLoading && historyItems.length > 0 && (
                                                <div className={'history-timeline'}>
                                                    {historyItems.map((item, index) => (
                                                        <div className={'history-item'} key={item.key}>
                                                            <div className={'history-marker'}>
                                                                <span className={'history-dot'} />
                                                                {index < historyItems.length - 1 && (
                                                                    <span className={'history-line'} />
                                                                )}
                                                            </div>
                                                            <div className={'history-content'}>
                                                                <div className={'history-date'}>
                                                                    {item.dateLabel}
                                                                    <span className={'history-weekday'}>{item.weekdayLabel}</span>
                                                                </div>
                                                                <div className={'history-entry'}>
                                                                    <div className={'history-avatar'}>
                                                                        {item.actorImageUrl ? (
                                                                            <img src={item.actorImageUrl} alt={item.actorName} />
                                                                        ) : (
                                                                            <span>{item.actorInitials}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className={'history-text'}>{item.action}</div>
                                                                </div>
                                                                {item.note && (
                                                                    <div className={'history-note'}>{item.note}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </section>
                                    )}
                                </div>
                                <div className={'event-modal-footer'}>
                                    <DefaultButton className={'event-modal-rounded-button'} onClick={() => setOpen(false)}
                                        disabled={awaitingPublish || awaitingSave}>Cancel</DefaultButton>
                                    {activeTab === 'details' && (
                                        <PrimaryButton
                                            className={'event-modal-rounded-button'}
                                            disabled={awaitingPublish || awaitingSave}
                                            onClick={handleEditToggle}
                                        >
                                            {isEditOpen ? 'Close Edit' : 'Edit Shift'}
                                        </PrimaryButton>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </AppInsightsErrorBoundary>
            </Modal>
        </>

    )

}

export default EventModal
